import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { WriteBatch } from '@angular/fire/firestore';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { lastValueFrom } from 'rxjs';
import { generateSku } from 'src/app/utils/generate-sku';
import { getSkuNumber } from 'src/app/utils/getSkuNumber';
import { AbcService } from '../abc/abc.service';
import { AuthService } from '../auth/auth.service';
import { CloudinaryService } from '../cloudinary/cloudinary.service';
import { NotificationService } from '../notifications/notifications.service';
import { environment } from '../../../environments/environment';
import CryptoJS from 'crypto-js';
import { HttpClient } from '@angular/common/http';

const MAX_BATCH_SIZE = 500;
@Injectable({
    providedIn: 'root'
})
export class ApiService {

    private readonly inc = firebase.firestore.FieldValue.increment;

    private ts() {
        return firebase.firestore.FieldValue.serverTimestamp();
    }

    private costDocPath(matKey: string, locKey: string) {
        // ¡OJO! NADA de area aquí.
        return `accounts_abc/${this.auth.account_key}/inventory_cost/${matKey}_${locKey}`;
    }

    constructor(
        public db: AngularFirestore,
        public firestore: AngularFirestore,
        public auth: AuthService,
        public abc: AbcService,
        public cloudinaryService: CloudinaryService,
        public cloudinary: CloudinaryService,
        public notificationService: NotificationService,
        private http: HttpClient,
    ) {
    }

    getAllDocuments(collection: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.db.collection(collection)
                .get()
                .toPromise()
                .then((querySnapshot) => {
                    let arr: any = [];
                    if (querySnapshot !== undefined) {
                        querySnapshot.forEach(function (doc) {
                            var obj = JSON.parse(JSON.stringify(doc.data()));
                            obj.$key = doc.id
                            arr.push(obj);
                        });

                        if (arr.length > 0) {
                            resolve(arr);
                        } else {
                            resolve(null);
                        }
                    }

                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    }

    getDocument(collection: any, documentId: any) {
        return new Promise((resolve, reject) => {
            this.db.collection(collection).doc(documentId)
                .get()
                .toPromise()
                .then((snapshot) => {
                    if (snapshot) {
                        let doc: any = snapshot.data();
                        doc.$key = snapshot.id;
                        resolve(doc);
                    }
                }).catch((error: any) => {
                    reject(error);
                });
        })
    }

    deleteDocument(collectionName: string, docID: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.db
                .collection(collectionName)
                .doc(docID)
                .delete()
                .then((obj: any) => {
                    resolve(obj);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    }

    addDocument(collectionName: string, dataObj: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.db.collection(collectionName).add(dataObj)
                .then((obj: any) => {
                    resolve(obj);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    }

    updateDocument(collectionName: string, docID: string, dataObj: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.db
                .collection(collectionName)
                .doc(docID)
                .update(dataObj)
                .then((obj: any) => {
                    resolve(obj);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    }

    // Los docs de `inventory` guardan una copia de category_key al crearse (compra/transferencia/producción),
    // por lo que un cambio de categoría en el material (o su borrado) queda huérfano si no se propaga aquí.
    updateInventoryCategoryForMaterial(materialKey: string, categoryKey: string): Promise<any> {
        return new Promise(async (resolve, reject) => {
            try {
                const accountKey = this.auth.account_key;
                const snapshot = await this.firestore.firestore
                    .collection(`accounts_abc/${accountKey}/inventory`)
                    .where('material_key', '==', materialKey)
                    .get();

                if (snapshot.empty) {
                    resolve(true);
                    return;
                }

                let batch = this.firestore.firestore.batch();
                let operationsCount = 0;

                for (const doc of snapshot.docs) {
                    batch.update(doc.ref, { category_key: categoryKey || null });
                    operationsCount++;
                    if (operationsCount >= MAX_BATCH_SIZE) {
                        await batch.commit();
                        batch = this.firestore.firestore.batch();
                        operationsCount = 0;
                    }
                }

                await batch.commit();
                resolve(true);
            } catch (error) {
                reject(error);
            }
        });
    }

    setDocument(collectionName: string, docID: string, dataObj: any) {
        return new Promise((resolve, reject) => {
            this.db
                .collection(collectionName)
                .doc(docID)
                .set(dataObj)
                .then((obj: any) => {
                    resolve(obj)
                })
                .catch((error: any) => {
                    reject(error);
                })
        })
    }

    getRef(collection: string) {
        return this.db.collection(collection);
    }

    getPaginations(collectionName: string, order: string, pageSize: number, lastVisibleDoc: any): Promise<any> {
        return new Promise((resolve, reject) => {

            let query = this.db.collection(collectionName, ref => {
                let queryRef = ref.orderBy(order, 'desc').limit(pageSize);
                if (lastVisibleDoc) {
                    queryRef = queryRef.startAfter(lastVisibleDoc); // Empezar después del último documento visible
                }
                return queryRef;
            });


            query.get()
                .toPromise()
                .then((querySnapshot) => {
                    let docs: any = [];
                    if (querySnapshot !== undefined) {
                        querySnapshot.forEach(function (doc) {
                            let data_obj: any = doc.data();
                            if (data_obj.creation_date) {
                                data_obj.creation_date = data_obj.creation_date.toDate();
                            }
                            var obj = JSON.parse(JSON.stringify(data_obj));
                            obj.$key = doc.id;
                            docs.push(obj);
                        });
                        const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
                        resolve({ docs, lastVisible });
                    }
                })
                .catch((error) => {
                    reject(error);
                });

        })
    }

    async addMaterialsPurchase(purchase: any, items: any[], files: string[] | []) {

        try {
            const accountKey = this.auth.account_key;
            const user = this.auth.user_resume;
            const firestore = this.firestore.firestore;

            const purchase_key = this.firestore.createId();
            purchase.user = user;
            purchase.creation_date = this.ts();

            await firestore.runTransaction(async (tx) => {
                const purchaseRef = this.firestore
                    .collection(`accounts_abc/${accountKey}/purchases`)
                    .doc(purchase_key).ref;

                // --------------------------------------------------------
                // 1) LECTURAS + CÁLCULOS (sin writes)
                // --------------------------------------------------------
                const plans: any[] = [];

                for (const item of items) {
                    const material = item.material;
                    const area = item.area;
                    const locKey = purchase.location_key;
                    const areaKey = area.$key;

                    const invRef = firestore.doc(`accounts_abc/${accountKey}/inventory/${material.$key}_${locKey}_${areaKey}`);



                    const costRef = firestore.doc(`accounts_abc/${this.auth.account_key}/inventory_cost/${material.$key}_${locKey}`);

                    // READS (todo antes de cualquier write)
                    const [invSnap, costSnap] = await Promise.all([tx.get(invRef), tx.get(costRef)]);

                    // Unidad base por área
                    const invUnit = invSnap.exists
                        ? ((((invSnap.data() as any)?.metric)?.symbol) || material.metric?.symbol || 'pzs')
                        : (material.metric?.symbol || 'pzs');

                    // Cantidad normalizada a unidad base
                    const qtyBase = Number(this.abc.convertMeasure(
                        material.measure,
                        Number(item.quantity || 0),
                        item.price_metric?.symbol || material.metric?.symbol || invUnit,
                        invUnit
                    ));
                    if (!(qtyBase > this.EPS)) {
                        throw new Error(`Cantidad inválida para la compra de ${material.name}.`);
                    }

                    const unitPrice = Number(item.price || 0);

                    const prevAvail = invSnap.exists ? Number(((invSnap.data() as any)['available']) || 0) : 0;
                    const newAvail = this.to6(prevAvail + qtyBase);

                    // Pools y promedios previos (para WAC)
                    const costExists = costSnap.exists;
                    const prevPool = costExists ? Number(((costSnap.data() as any)['qty_pool']) || 0) : 0;
                    const prevAvg = costExists ? Number(((costSnap.data() as any)['avg_price']) || 0) : 0;

                    // Preparamos lote (aún sin writes)
                    const batchId = this.firestore.createId();
                    const batchRef = invRef.collection('batches').doc(batchId);
                    const batchCode = `PUR-${new Date().toISOString().slice(2, 10).replace(/-/g, '')}-${batchId.slice(-4)}`;
                    const batchData = {
                        batch_key: batchId,
                        entry_date: this.ts(),
                        expiration_date: item.expiration_date || null,
                        quantity: this.to6(qtyBase),
                        unit_price: unitPrice,
                        status: 'active',
                        created_from: 'purchase',
                        source_key: purchase_key,
                        batch_code: batchCode
                    };

                    plans.push({
                        material, areaKey, locKey,
                        invRef, invSnapExists: invSnap.exists,
                        invUnit, prevAvail, newAvail,
                        batchRef, batchData,
                        wac_prevPool: prevPool,
                        wac_prevAvg: prevAvg,
                        unitPrice,
                        costRef, costExists,
                        // detalle para log
                        logDetailBase: {
                            batch_key: batchId,
                            batch_code: batchCode,
                            unit_price: unitPrice,
                            purchase_quantity: Number(item.purchase_quantity || item.quantity || 0),
                            purchase_price: Number(item.purchase_price || unitPrice),
                            purchase_metric: item.purchase_measure || material.metric || null,
                            expiration_date: item?.expiration_date ?? (Number(item?.expiration_days) || null)
                        }
                    });
                }

                // --------------------------------------------------------
                // 2) ESCRITURAS (sin ninguna lectura adicional)
                // --------------------------------------------------------
                tx.set(purchaseRef, purchase);

                for (const p of plans) {
                    // INVENTORY por área (sin price)
                    if (p.invSnapExists) {
                        tx.update(p.invRef, { available: p.newAvail, last_purchase: this.ts() });
                    } else {
                        tx.set(p.invRef, {
                            material_key: p.material.$key,
                            name: p.material.name,
                            sku: p.material.sku || '',
                            area_key: p.areaKey,
                            location_key: p.locKey,
                            available: p.newAvail,
                            metric: p.material.metric || null,
                            measure: p.material.measure || null,
                            type: p.material.type || null,
                            category_key: p.material.category_key || null,
                            account: accountKey,
                            creation_date: this.ts(),
                            last_purchase: this.ts()
                        });
                    }

                    // LOTE histórico (unit_price vive aquí)
                    tx.set(p.batchRef, p.batchData);

                    // INVENTORY_COST (WAC) - sin lecturas
                    const entryRes = this.applyWacCoreWriteOnly(
                        tx,
                        p.costRef,
                        p.material.$key, p.locKey,
                        p.costExists,
                        p.wac_prevPool, p.wac_prevAvg,
                        p.batchData.quantity,
                        p.unitPrice,
                        'purchase',
                        purchase_key
                    );

                    // LOG movimiento (con campos obligatorios SIEMPRE presentes)
                    this.logInventoryMovementBatch(tx, {
                        type: 'purchase',
                        source_key: purchase_key,
                        material: p.material,
                        quantity_before: p.prevAvail,
                        quantity_after: p.newAvail,
                        quantity_changed: this.to6(p.newAvail - p.prevAvail),
                        measure: p.invUnit,
                        location_key: p.locKey,
                        area_key: p.areaKey,
                        user,
                        detail: {
                            ...p.logDetailBase,
                            avg_price_location_before: entryRes.prevAvg,
                            avg_price_location_after: entryRes.newAvg,
                            qty_pool_before: entryRes.prevPool,
                            qty_pool_after: entryRes.newPool,
                            last_unit_price: entryRes.appliedUnit
                        }
                    });
                }
            });

            // Adjuntos (FUERA de la transacción)
            if (files?.length) {
                const purchase_receipts_url = await this.uploadFileToCloudinary(files, 'purchase_receipts');
                await this.firestore
                    .collection(`accounts_abc/${accountKey}/purchases`)
                    .doc(purchase_key)
                    .update({ purchase_receipts_url });
            }

            return true;
        } catch (err) {
            console.error('Error en addMaterialsPurchase:', err);
            throw err;
        }
    }

    async uploadFileToCloudinary(files: string[], folder: string) {
        const uploadedUrls: string[] = [];

        for (const file of files) {
            const url = await this.cloudinary.uploadImage(file, folder);
            uploadedUrls.push(url);
        }
        return uploadedUrls;
    }

    async addPendingPayments(
        purchaseKey: string,
        payments: Array<any>,
        status: 'paid' | 'pending',
        files: string[] | []
    ): Promise<{ completed: boolean; payments: any[] }> {
        try {

            const accountKey = this.auth.account_key;
            const docRef = this.firestore.collection(`accounts_abc/${accountKey}/purchases`).doc(purchaseKey).ref;
            const snapshot = await docRef.get();

            const updateData: any = { payments };

            if (status === 'paid') {
                updateData.status = 'paid';
            }
            if (files?.length) {
                const purchase: any = snapshot.data();
                const previous_receipts = Array.isArray(purchase.purchase_receipts_url)
                    ? purchase.purchase_receipts_url
                    : [];
                const current_recipts = await this.uploadFileToCloudinary(files, 'purchase_receipts');
                updateData.purchase_receipts_url = [...current_recipts, ...previous_receipts]
            }

            await docRef.update(updateData);

            return {
                completed: status === 'paid',
                payments,
            };
        } catch (err) {
            console.error('Error en addPendingPayments:', err);
            throw err;
        }
    }

    async addEquipmentPurchase(purchase: any, items: any) {
        try {
            const user = this.auth.user_resume;
            let batch = this.firestore.firestore.batch();
            let operationsCount = 0;

            const purchase_key = this.firestore.createId();
            purchase.user = this.auth.user_resume;
            purchase.creation_date = this.ts();

            batch.set(this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/equipment/purchases`).doc(purchase_key),
                purchase
            );

            operationsCount++;

            const historyPromises: any[] = [];

            for (let item of items) {
                console.log('ITEM', item);

                const item_key = this.firestore.createId();
                const equipment = item.equipment;
                const area = item.area;

                item.purchase_key = purchase_key;
                item.creation_date = this.ts();
                batch.set(this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/equipment/purchase_items`).doc(item_key), item);
                operationsCount++;

                const inventoryId = `${item.equipment.$key}_${purchase.location_key}_${item.area.$key}`;
                const inventoryRef = this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/equipment/inventory`).doc(inventoryId);

                const batchId = this.firestore.createId();
                let batchCode = `PUR-${new Date().toISOString().slice(2, 10).replace(/-/g, "")}-${batchId.slice(-4)}`;

                const batchData = {
                    batch_key: batchId,
                    entry_date: this.ts(),
                    expiration_date: item.expiration_date || null, // Si el material tiene fecha de vencimiento
                    quantity: item.quantity,
                    unit_price: item.price,
                    status: "active",
                    created_from: 'purchase',
                    source_key: purchase_key,
                    batch_code: batchCode
                };

                const batchRef = inventoryRef.collection("batches").doc(batchId);

                batch.set(batchRef, batchData);
                operationsCount++;

                const snapshot = await inventoryRef.get();

                let newPrice = item.price;
                let updatedAvailable = item.quantity;
                let previousAvailable = 0;
                let previousPrice = 0;

                if (!snapshot.exists) {
                    // Si el artculo no existe en el inventario, agregarlo como nuevo
                    const item_inventory = {
                        equipment_key: item.equipment.$key,
                        name: item.equipment.name,
                        sku: item.equipment.sku,
                        category_key: item.equipment.category_key,
                        area_key: item.area.$key,
                        location_key: purchase.location_key,
                        last_purchase: this.ts(),
                        available: item.quantity,
                        price: item.price,
                        area: item.area,
                        type: item.equipment.type
                    };

                    batch.set(inventoryRef, item_inventory);
                    operationsCount++;

                } else {

                    const existingInventory: any = snapshot.data();
                    previousAvailable = existingInventory.available || 0;
                    previousPrice = existingInventory.price || 0;

                    updatedAvailable = previousAvailable + item.quantity;

                    const totalCost = (previousPrice * previousAvailable) + (item.price * item.quantity);
                    newPrice = totalCost / updatedAvailable;

                    batch.update(inventoryRef, {
                        available: updatedAvailable,
                        price: newPrice,
                        last_purchase: this.ts()
                    });
                    operationsCount++;
                }

                const historyRef = this.getRef(`accounts_abc/${this.auth.account_key}/equipment/history`).ref.doc();

                historyPromises.push(
                    historyRef.set({
                        equipment_key: item.equipment.$key,
                        item_name: item.equipment.name,
                        batch_key: batchId,
                        quantity_added: item.quantity,
                        price: item.price,
                        new_average_price: newPrice,
                        date: this.ts(),
                        location_key: purchase.location_key,
                        area_key: item.area.$key,
                        event: "purchase_added_to_inventory",
                        user: this.auth.user_resume
                    })
                );

                // Log en inventory_movements
                this.logInventoryMovementBatch(batch, {
                    type: 'purchase',
                    source_key: purchase_key,
                    equipment,
                    quantity_before: previousAvailable,
                    quantity_after: updatedAvailable,
                    quantity_changed: item.quantity,
                    measure: 'pzs',
                    location_key: purchase.location_key,
                    area_key: area.$key,
                    user,
                    detail: {
                        batch_key: batchId,
                        batch_code: batchCode,
                        unit_price: item.price,
                        expiration_date: item.expiration_date || null
                    }
                });

                // Commit final para las operaciones restantes en el batch
                if (operationsCount > 0) {
                    await batch.commit();
                }

                await Promise.all(historyPromises);

                return true;

            }

            // Commit final para las operaciones restantes en el batch
            if (operationsCount > 0) {
                await batch.commit();
            }

            await Promise.all(historyPromises);

            return true;
        } catch (err) {
            console.error('Error en addEquipmentPurchase:', err);
            throw err;
        }

    }

    addProduction(materials: any, collection: 'productions' | 'sample_productions') {

        return new Promise(async (resolve, reject) => {
            try {
                let batch = this.firestore.firestore.batch();

                let operationsCount = 0;

                for (let production of materials) {
                    let production_key = this.firestore.createId();
                    production.creation_date = new Date();
                    production.production_key = production.$key;
                    production.status = 'on_hold'
                    production.user = this.auth.user_resume;
                    production.location_key = this.auth.location_key;
                    production.area_key = this.auth.area_key;
                    delete production.$key;
                    batch.set(this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/${collection}`).doc(production_key), production);
                    operationsCount++;
                    if (operationsCount >= MAX_BATCH_SIZE) {
                        await batch.commit();
                        batch = this.firestore.firestore.batch();
                        operationsCount = 0;
                    }
                };

                await batch.commit();
                resolve(true);
            } catch (error) {
                reject(error);
            }

        })
    }

    confirmProductionOrder(order: any) {
        return new Promise(async (resolve, reject) => {
            try {
                console.log(order);

                let batch = this.firestore.firestore.batch();

                let operationsCount = 0;

                batch.update(this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/production_orders`).doc(order.$key), {
                    materials: order.materials,
                    status: 'accepted',
                    accepted_date: this.ts()
                });

                for (let production of order.materials) {
                    let production_key = this.firestore.createId();
                    production.creation_date = this.ts();
                    production.material_key = production.$key;
                    production.status = 'on_hold'
                    production.user = this.auth.user_resume;
                    production.location_key = this.auth.location_key;
                    production.area_key = this.auth.area_key;
                    production.quantity = production.final_quantity;
                    delete production.final_quantity;
                    delete production.$key;

                    batch.set(this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/productions`).doc(production_key), production);
                    operationsCount++;
                    if (operationsCount >= MAX_BATCH_SIZE) {
                        await batch.commit();
                        batch = this.firestore.firestore.batch();
                        operationsCount = 0;
                    }
                }

                await batch.commit();
                resolve(true);

            } catch (error) {
                console.error('Error al iniciar producción:', error);
                throw error;
            }
        });
    }

    async startProduction(
        material_production: any,
        collection: 'productions' | 'sample_productions',
        user: any
    ): Promise<boolean> {
        try {
            const accountKey = this.auth.account_key;
            const firestore = this.firestore.firestore;
            const locationKey = this.auth.location_key;
            const areaKey = this.auth.area_key;

            const new_recipe: any[] = [];

            for (const material of (material_production.recipe || [])) {
                const mat = material.material;
                const materialKey = mat.$key || mat.material_key;

                const mt = mat?.movement_type || 'inventory_usage';

                // 🚫 Solo referencia: no tocar inventario ni WAC ni lotes
                if (mt !== 'inventory_usage') {
                    new_recipe.push({
                        ...material,
                        ignored_in_production: true,
                        ignored_reason: `movement_type=${mt}`,
                        // opcional: dejar una marca de que NO se convirtió ni costeo
                        converted_amount_needed: 0,
                        used_batches: [],
                        total_cost_snapshot: 0,
                        price_per_unit_snapshot: 0,
                        avg_price_location_used: 0,
                    });
                    continue;
                }


                if (!materialKey || !material.inventory?.material_key) {
                    console.warn('startProduction: material sin key o sin inventory.material_key', material);
                    continue;
                }

                // INVENTORY (por área)
                const invPath = `accounts_abc/${accountKey}/inventory/${material.inventory.material_key}_${locationKey}_${areaKey}`;
                const invRef = firestore.doc(invPath);

                // INVENTORY_COST (por ubicación)
                const costRef = firestore.doc(this.costDocPath(materialKey, locationKey));

                // ─────────────────────────────
                // PRE-LECTURA FIFO de lotes (fuera de la TX, solo refs)
                // ─────────────────────────────
                const preSnap = await firestore
                    .collection(`${invPath}/batches`)
                    .where('status', '==', 'active')
                    .orderBy('entry_date', 'asc')
                    .get();

                const fifoRefs = preSnap.docs.map(d => d.ref);

                await firestore.runTransaction(async (tx) => {
                    // ───────────────
                    // 1) LECTURAS
                    // ───────────────
                    const invSnapPromise = tx.get(invRef);
                    const costSnapPromise = tx.get(costRef);
                    const batchSnapPromises = fifoRefs.map(r => tx.get(r));

                    const [invSnap, costSnap, ...batchSnaps] = await Promise.all([
                        invSnapPromise,
                        costSnapPromise,
                        ...batchSnapPromises
                    ]);

                    if (!invSnap.exists) {
                        throw new Error(`El inventario del material ${mat.name} no existe (${invPath}).`);
                    }

                    const inv: any = invSnap.data();
                    const invUnit = inv?.metric?.symbol || mat?.metric?.symbol || 'pzs';

                    // cantidad requerida en unidad base (inventario área)
                    const requiredBase = Number(this.abc.convertMeasure(
                        mat.measure,
                        Number(material.amount_needed || 0),
                        material.metric?.symbol || mat.metric?.symbol || invUnit,
                        invUnit
                    ));

                    if (requiredBase <= this.EPS) {
                        throw new Error(`Cantidad requerida inválida para ${mat.name}`);
                    }

                    // CONSUMO FIFO de lotes
                    let remaining = requiredBase;
                    let totalConsumedBase = 0;
                    let totalCostValue = 0;
                    const usedBatches: Array<{
                        batch_code?: string;
                        batch_key: string;
                        used_from_batch: number;
                        unit_price: number;
                    }> = [];
                    const writes: Array<{ ref: any; data: any }> = [];

                    for (let i = 0; i < batchSnaps.length; i++) {
                        const snap = batchSnaps[i];
                        if (remaining <= this.EPS) break;
                        if (!snap.exists) continue;

                        const b = snap.data() as any;
                        const cur = Number(b.quantity || 0);
                        if (cur <= this.EPS) continue;

                        const take = Math.min(cur, remaining);
                        const newQ = this.to6(cur - take);

                        const patch: any = {
                            quantity: newQ,
                            status: newQ <= this.EPS ? 'depleted' : 'active'
                        };
                        if (newQ <= this.EPS) patch.depleted_date = this.ts();

                        writes.push({ ref: fifoRefs[i], data: patch });

                        usedBatches.push({
                            batch_code: b.batch_code || '',
                            batch_key: snap.id,
                            used_from_batch: take,
                            unit_price: Number(b.unit_price || 0)
                        });

                        totalConsumedBase = this.to6(totalConsumedBase + take);
                        totalCostValue = this.to6(totalCostValue + take * Number(b.unit_price || 0));
                        remaining = this.to6(remaining - take);
                    }

                    if (remaining > this.EPS) {
                        throw new Error(`No hay suficiente inventario de lotes para ${mat.name}.`);
                    }

                    const beforeAvail = Number(inv.available || 0);
                    if (beforeAvail + this.EPS < totalConsumedBase) {
                        throw new Error(`Disponible insuficiente en área para ${mat.name}`);
                    }

                    // ─────────────────────────────
                    // 2) WAC EXIT (INVENTORY_COST)
                    // ─────────────────────────────
                    const costExists = costSnap.exists;
                    const costData: any = costExists ? costSnap.data() : null;
                    const prevPool = costExists ? Number(costData?.qty_pool || 0) : 0;
                    const prevAvg = costExists ? Number(costData?.avg_price || 0) : 0;

                    const wac = this.applyWacExitCoreWriteOnly(
                        tx,
                        costRef,
                        materialKey,
                        locationKey,
                        costExists,
                        prevPool,
                        prevAvg,
                        totalConsumedBase,
                        'production',
                        material_production.$key
                    );

                    // ───────────────
                    // 3) ESCRITURAS
                    // ───────────────

                    // Actualizar lotes
                    for (const w of writes) {
                        tx.update(w.ref, w.data);
                    }

                    // Actualizar available de inventario
                    tx.update(invRef, { available: this.inc(-totalConsumedBase) });

                    const afterAvail = this.to6(beforeAvail - totalConsumedBase);

                    // LOG movimiento (start production) con avg_price_location (WAC)
                    this.logInventoryMovementBatch(tx, {
                        type: 'production_start',
                        source_key: material_production.$key,
                        material: mat,
                        quantity_before: beforeAvail,
                        quantity_after: afterAvail,
                        quantity_changed: -totalConsumedBase,
                        measure: invUnit,
                        location_key: locationKey,
                        area_key: areaKey,
                        user: this.auth.user_resume,
                        avg_price_location: wac.newAvg,
                        detail: {
                            recipe_quantity: Number(material.amount_needed || 0),
                            converted_reserved: totalConsumedBase,
                            lots_cost_snapshot: totalCostValue,
                            used_batches: usedBatches,
                            avg_price_location_before: wac.prevAvg,
                            avg_price_location_after: wac.newAvg,
                            qty_pool_before: wac.prevPool,
                            qty_pool_after: wac.newPool
                        }
                    });

                    // Snapshot de “reserva” pero guardado DIRECTO en la producción (recipe)
                    const avgUsed = wac.prevAvg; // avg_price al momento de iniciar la producción
                    const costValueByWac = this.to6(avgUsed * totalConsumedBase);

                    new_recipe.push({
                        ...material,
                        converted_amount_needed: totalConsumedBase,
                        used_batches: usedBatches,
                        // snapshot usando precios de lotes (por si quieres verlo también)
                        total_cost_snapshot: totalCostValue,
                        price_per_unit_snapshot:
                            totalConsumedBase > this.EPS
                                ? this.to6(totalCostValue / totalConsumedBase)
                                : 0,
                        // snapshot usando WAC:
                        avg_price_location_used: avgUsed,
                        inv_unit_symbol: invUnit,
                        location_key: locationKey,
                        area_key: areaKey,
                        wac_snapshot: {
                            avg_price_before: wac.prevAvg,
                            avg_price_after: wac.newAvg,
                            qty_pool_before: wac.prevPool,
                            qty_pool_after: wac.newPool
                        }
                    });
                });
            }

            // Actualizar header de la producción con la receta ya enriquecida
            await firestore.runTransaction(async (tx) => {
                const productionRef = firestore
                    .collection(`accounts_abc/${accountKey}/${collection}`)
                    .doc(material_production.$key);
                tx.update(productionRef, {
                    status: 'in_production',
                    started_at: this.ts(),
                    recipe: new_recipe,
                    started_by: user
                });
            });

            return true;
        } catch (error) {
            console.error('Error en startProduction:', error);
            throw error;
        }
    }

    async completeProduction(
        production: any,
        _totalCost: any,
        _currentPerformance: any,
        collection: 'productions' | 'sample_productions',
        user: any
    ) {
        try {
            const accountKey = this.auth.account_key;
            const locationKey = this.auth.location_key;
            const areaKey = this.auth.area_key;
            const firestore = this.firestore.firestore; // compat native firestore

            const finalAdjustments: any[] = [];

            // ------------------------------
            // Helpers
            // ------------------------------
            const toNum = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);

            const getLineUnit = (m: any) =>
                String(m?.metric?.symbol || m?.inv_unit_symbol || m?.inventory?.metric?.symbol || 'pzs');

            const getMeasureType = (m: any) =>
                String(m?.material?.measure || m?.inventory?.measure || m?.measure || 'pzs');

            const convert = (measure: string, amount: number, from: string, to: string) => {
                if (!Number.isFinite(amount) || !amount) return 0;
                if (!from || !to || from === to) return amount;
                const out = Number(this.abc.convertMeasure(measure, amount, from, to));
                return Number.isFinite(out) ? out : 0;
            };

            const getUsedLine = (m: any) => toNum(m?.used ?? m?.amount_used ?? 0);

            const getNeededLine = (m: any) => {
                const direct = toNum(m?.amount_needed ?? 0);
                if (direct > 0) return direct;

                const q = toNum(m?.quantity ?? 0);
                const prodQty = toNum(production?.quantity ?? 1);
                return q > 0 ? (q * prodQty) : 0;
            };

            const getUsedConverted = (m: any) =>
                (m?.converted_amount_used != null) ? toNum(m?.converted_amount_used) : null;

            const getNeededConverted = (m: any) =>
                (m?.converted_amount_needed != null) ? toNum(m?.converted_amount_needed) : null;

            await firestore.runTransaction(async (tx: any) => {

                // -------------------------------------------------------
                // 1) LECTURAS: inventarios + costos + batches SOLO si deltaInv != 0
                // -------------------------------------------------------
                const recipeReads = await Promise.all(
                    (production.recipe || []).map(async (material: any) => {
                        if (!material || !material.material) return null;

                        const mt = material.material?.movement_type || 'inventory_usage';
                        if (mt !== 'inventory_usage') return null;

                        const materialKey = material.material.$key;
                        const inventoryId = `${materialKey}_${locationKey}_${areaKey}`;

                        const inventoryRef = firestore
                            .collection(`accounts_abc/${accountKey}/inventory`)
                            .doc(inventoryId);

                        const invSnap = await tx.get(inventoryRef);
                        if (!invSnap.exists) return null;

                        const invData: any = invSnap.data();
                        const invUnit = String(invData?.metric?.symbol || material?.inv_unit_symbol || 'pzs');

                        const measure = getMeasureType(material);
                        const lineUnit = getLineUnit(material);

                        const usedConv = getUsedConverted(material);
                        const usedInv = (usedConv != null)
                            ? usedConv
                            : convert(measure, getUsedLine(material), lineUnit, invUnit);

                        const neededConv = getNeededConverted(material);
                        const neededInv = (neededConv != null)
                            ? neededConv
                            : convert(measure, getNeededLine(material), lineUnit, invUnit);

                        const deltaInv = toNum(usedInv) - toNum(neededInv);

                        if (!Number.isFinite(deltaInv) || Math.abs(deltaInv) <= this.EPS) {
                            return null;
                        }

                        const costRef = firestore
                            .collection(`accounts_abc/${accountKey}/inventory_cost`)
                            .doc(`${materialKey}_${locationKey}`);

                        const costSnap = await tx.get(costRef);
                        const costExists = !!costSnap.exists;

                        // Pre-cargar batches solo cuando hace falta
                        let batchReads: any[] = [];

                        // deltaInv < 0 => devolver a batches utilizados
                        if (deltaInv < -this.EPS) {
                            batchReads = await Promise.all(
                                (material.used_batches || []).map(async (ub: any) => {
                                    const bRef = inventoryRef.collection('batches').doc(ub.batch_key);
                                    const bSnap = await tx.get(bRef);
                                    return { ub, bRef, bSnap };
                                })
                            );
                        }

                        // deltaInv > 0 => tomar extra FIFO activos (fallback query.get() por compat)
                        if (deltaInv > this.EPS) {
                            const q = inventoryRef.collection('batches')
                                .where('status', '==', 'active')
                                .orderBy('entry_date', 'asc');

                            const pre = await q.get(); // compat-safe
                            batchReads = pre.docs.map((d: any) => ({ bRef: d.ref, bSnap: d }));
                        }

                        return {
                            material,
                            inventoryRef,
                            invSnap,
                            invUnit,
                            batchReads,
                            costRef,
                            costSnap,
                            costExists,
                            deltaInv,
                            usedInv,
                            neededInv,
                            lineUnit,
                        };
                    })
                );

                // Outputs reads
                const outputs = production.outputs || [];
                const outputReads = await Promise.all(
                    outputs.map(async (out: any) => {
                        const materialKey = out.material_key || out.material?.$key;
                        const outputId = `${materialKey}_${locationKey}_${areaKey}`;

                        const invRef = firestore
                            .collection(`accounts_abc/${accountKey}/inventory`)
                            .doc(outputId);

                        const invSnap = await tx.get(invRef);

                        const costRef = firestore
                            .collection(`accounts_abc/${accountKey}/inventory_cost`)
                            .doc(`${materialKey}_${locationKey}`);

                        const costSnap = await tx.get(costRef);
                        const costExists = !!costSnap.exists;

                        return { out, invRef, invSnap, costExists, costRef, costSnap };
                    })
                );

                // -------------------------------------------------------
                // 2) ESCRITURAS: AJUSTES DE INSUMOS (por deltaInv)
                // -------------------------------------------------------
                for (const r of recipeReads) {
                    if (!r) continue;

                    const { material, inventoryRef, invSnap, invUnit, costRef, costSnap, costExists, deltaInv } = r;
                    let batchReads = r.batchReads || [];

                    const invData: any = invSnap.data();
                    let remaining = toNum(deltaInv);

                    const devPercent = toNum(material.deviation) || 0;

                    const detailAdj: any = {
                        material_key: material.material.$key,
                        name: material.material.name,
                        deviation: devPercent,
                        adjusted_quantity: remaining,
                        type: remaining > 0 ? 'extra_used' : 'returned',
                        inv_unit_symbol: invUnit,
                        batches: [] as any[]
                    };

                    const prevPool = costExists ? toNum((costSnap.data() as any)?.qty_pool) : 0;
                    const prevAvg = costExists ? toNum((costSnap.data() as any)?.avg_price) : 0;

                    // ---------- DEVOLVER (deltaInv < 0) ----------
                    if (remaining < -this.EPS) {
                        if (!Array.isArray(batchReads) || batchReads.length === 0) {
                            throw new Error(`No se puede devolver ${material.material.name}: faltan used_batches.`);
                        }

                        let returnCostValue = 0;

                        for (const item of batchReads) {
                            const { ub, bRef, bSnap } = item;
                            if (!bSnap.exists || remaining >= -this.EPS) break;

                            const bData = bSnap.data() as any;

                            const giveBack = Math.min(toNum(ub?.used_from_batch), Math.abs(remaining));
                            if (giveBack <= this.EPS) continue;

                            const newQ = this.to6(toNum(bData.quantity) + giveBack);
                            const patch: any = { quantity: newQ };

                            if (bData.status === 'depleted' && newQ > this.EPS) {
                                patch.status = 'active';
                                patch.depleted_date = null;
                                patch.activation_date = this.ts();
                            }

                            tx.update(bRef, patch);

                            remaining = this.to6(remaining + giveBack);
                            returnCostValue = this.to6(returnCostValue + giveBack * toNum(bData.unit_price));

                            detailAdj.batches.push({
                                batch_key: ub.batch_key,
                                quantity_returned: giveBack,
                                unit_price: toNum(bData.unit_price)
                            });
                        }

                        const applied = this.to6(toNum(deltaInv) - remaining); // negativo
                        if (Math.abs(applied) <= this.EPS) continue;

                        const before = toNum(invData.available);
                        const after = this.to6(before - applied); // - (neg) => suma

                        tx.update(inventoryRef, { available: after });

                        const qtyIn = this.to6(Math.abs(applied));
                        const returnUnitPrice = qtyIn > this.EPS ? this.to6(returnCostValue / qtyIn) : prevAvg;

                        const wacEntry = this.applyWacCoreWriteOnly(
                            tx, costRef, material.material.$key, locationKey,
                            costExists, prevPool, prevAvg,
                            qtyIn, returnUnitPrice,
                            'production_adjustment', production.$key
                        );

                        this.logInventoryMovementBatch(tx, {
                            type: 'production_adjustment',
                            source_key: production.$key,
                            material: material.material,
                            quantity_before: before,
                            quantity_after: after,
                            quantity_changed: this.to6(after - before),
                            measure: invUnit,
                            location_key: locationKey,
                            area_key: areaKey,
                            user,
                            avg_price_location: wacEntry.newAvg,
                            detail: {
                                adjustment_type: 'returned',
                                production_key: production.$key,
                                deviation_percent: devPercent,
                                requested_adjustment: deltaInv,
                                applied_adjustment: applied,
                                inv_unit_symbol: invUnit,
                                batches: detailAdj.batches,
                                wac: {
                                    mode: 'entry',
                                    qty_in: qtyIn,
                                    unit_price_in: returnUnitPrice,
                                    avg_price_before: wacEntry.prevAvg,
                                    avg_price_after: wacEntry.newAvg,
                                    qty_pool_before: wacEntry.prevPool,
                                    qty_pool_after: wacEntry.newPool
                                }
                            }
                        });

                        finalAdjustments.push(detailAdj);
                        continue;
                    }

                    // ---------- TOMAR EXTRA (deltaInv > 0) ----------
                    if (remaining > this.EPS) {

                        const beforeAvail = toNum(invData.available);

                        // validación clara
                        if (beforeAvail + this.EPS < toNum(deltaInv)) {
                            throw new Error(
                                `Inventario insuficiente: ${material.material.name}. ` +
                                `Disponible: ${this.to6(beforeAvail)} ${invUnit}. ` +
                                `Extra requerido: ${this.to6(deltaInv)} ${invUnit}.`
                            );
                        }

                        // si por alguna razón no vino batchReads, reconsultar (compat-safe)
                        if (!Array.isArray(batchReads) || batchReads.length === 0) {
                            const q = inventoryRef.collection('batches')
                                .where('status', '==', 'active')
                                .orderBy('entry_date', 'asc');

                            const pre = await q.get();
                            batchReads = pre.docs.map((d: any) => ({ bRef: d.ref, bSnap: d }));
                        }

                        for (const item of batchReads) {
                            const { bRef, bSnap } = item;
                            if (!bSnap.exists) continue;

                            const bd: any = bSnap.data();
                            const cur = toNum(bd.quantity);

                            if (remaining <= this.EPS) break;
                            if (cur <= this.EPS) continue;

                            const take = Math.min(cur, remaining);
                            if (take <= this.EPS) continue;

                            const newQ = this.to6(cur - take);
                            const patch: any = {
                                quantity: newQ,
                                status: newQ <= this.EPS ? 'depleted' : 'active'
                            };
                            if (newQ <= this.EPS) patch.depleted_date = this.ts();

                            tx.update(bRef, patch);

                            remaining = this.to6(remaining - take);

                            detailAdj.batches.push({
                                batch_key: bRef.id,
                                quantity_taken: take,
                                unit_price: toNum(bd.unit_price)
                            });
                        }

                        // si no alcanzó -> error
                        if (remaining > this.EPS) {
                            throw new Error(
                                `Inventario insuficiente: ${material.material.name}. ` +
                                `Faltan: ${this.to6(remaining)} ${invUnit}.`
                            );
                        }

                        const applied = this.to6(toNum(deltaInv));

                        const before = toNum(invData.available);
                        const after = this.to6(before - applied);

                        if (after + this.EPS < 0) {
                            throw new Error(
                                `Inventario insuficiente: ${material.material.name}. ` +
                                `Disponible: ${this.to6(before)} ${invUnit}. ` +
                                `Intentaste descontar: ${this.to6(applied)} ${invUnit}.`
                            );
                        }

                        tx.update(inventoryRef, { available: after });

                        const qtyOut = this.to6(applied);

                        const wacExit = this.applyWacExitCoreWriteOnly(
                            tx, costRef, material.material.$key, locationKey,
                            costExists, prevPool, prevAvg,
                            qtyOut, 'production_adjustment', production.$key
                        );

                        this.logInventoryMovementBatch(tx, {
                            type: 'production_adjustment',
                            source_key: production.$key,
                            material: material.material,
                            quantity_before: before,
                            quantity_after: after,
                            quantity_changed: this.to6(after - before),
                            measure: invUnit,
                            location_key: locationKey,
                            area_key: areaKey,
                            user,
                            avg_price_location: wacExit.newAvg,
                            detail: {
                                adjustment_type: 'extra_used',
                                production_key: production.$key,
                                deviation_percent: devPercent,
                                requested_adjustment: deltaInv,
                                applied_adjustment: applied,
                                inv_unit_symbol: invUnit,
                                batches: detailAdj.batches,
                                wac: {
                                    mode: 'exit',
                                    qty_out: qtyOut,
                                    avg_price_before: wacExit.prevAvg,
                                    avg_price_after: wacExit.newAvg,
                                    qty_pool_before: wacExit.prevPool,
                                    qty_pool_after: wacExit.newPool
                                }
                            }
                        });

                        finalAdjustments.push(detailAdj);
                        continue;
                    }
                }

                // -------------------------------------------------------
                // 3) ESCRITURAS: OUTPUTS
                // -------------------------------------------------------
                for (const o of outputReads) {
                    const { out, invRef, invSnap, costExists, costRef, costSnap } = o;

                    const materialKey = out.material_key || out.material?.$key;
                    const metric = out.metric || out.material?.metric;
                    const measure = out.measure || out.material?.measure;

                    const sourceMetric = out.metric?.symbol;
                    const targetMetric = invSnap.exists ? invSnap.data()?.metric?.symbol : (sourceMetric || 'pzs');

                    let adjustedPerformance = toNum(out.current_performance);
                    if (sourceMetric && targetMetric && sourceMetric !== targetMetric) {
                        adjustedPerformance = toNum(this.abc.convertMeasure(
                            measure,
                            adjustedPerformance,
                            sourceMetric,
                            targetMetric
                        ));
                    }

                    const qtyIn = this.to6(adjustedPerformance);
                    if (!Number.isFinite(qtyIn) || qtyIn <= this.EPS) continue;

                    const batchRef = invRef.collection('batches').doc();

                    let quantityBefore = 0;
                    let newStock = qtyIn;

                    if (invSnap.exists) {
                        quantityBefore = toNum(invSnap.data().available);
                        newStock = this.to6(quantityBefore + qtyIn);
                        tx.update(invRef, { available: newStock, last_production: this.ts() });
                    } else {
                        tx.set(invRef, {
                            material_key: materialKey,
                            name: out.name || out.material?.name,
                            available: qtyIn,
                            last_production: this.ts(),
                            sku: out.sku || out.material?.sku,
                            category_key: out.category_key || out.material?.category_key,
                            metric,
                            measure,
                            area_key: areaKey,
                            location_key: locationKey,
                            account: accountKey,
                            type: 'semi-finished',
                            creation_date: this.ts(),
                        });
                    }

                    const batchCode = `PROD-${new Date().toISOString().slice(2, 10).replace(/-/g, '')}-${batchRef.id.slice(-4)}`;

                    const totalCostOut = toNum(out.total_cost);
                    const unitPrice = qtyIn > this.EPS ? this.to6(totalCostOut / qtyIn) : 0;

                    const batchDetails = {
                        batch_key: batchRef.id,
                        entry_date: this.ts(),
                        expiration_date: production.expiration_date || null,
                        quantity: qtyIn,
                        unit_price: unitPrice,
                        status: 'active',
                        production_key: production.$key,
                        created_from: 'production',
                        source_key: production.$key,
                        batch_code: batchCode
                    };

                    tx.set(batchRef, batchDetails);

                    const prevPool = costExists ? toNum((costSnap.data() as any)?.qty_pool) : 0;
                    const prevAvg = costExists ? toNum((costSnap.data() as any)?.avg_price) : 0;

                    const entryRes = this.applyWacCoreWriteOnly(
                        tx, costRef, materialKey, locationKey,
                        costExists, prevPool, prevAvg,
                        qtyIn, unitPrice,
                        'production', production.$key
                    );

                    this.logInventoryMovementBatch(tx, {
                        type: 'production',
                        source_key: production.$key,
                        material: production,
                        quantity_before: quantityBefore,
                        quantity_after: newStock,
                        quantity_changed: this.to6(newStock - quantityBefore),
                        measure: targetMetric,
                        location_key: locationKey,
                        area_key: areaKey,
                        user,
                        detail: {
                            ...batchDetails,
                            avg_price_location_before: entryRes.prevAvg,
                            avg_price_location_after: entryRes.newAvg,
                            qty_pool_before: entryRes.prevPool,
                            qty_pool_after: entryRes.newPool,
                            last_unit_price: entryRes.appliedUnit
                        }
                    });
                }

                // -------------------------------------------------------
                // 4) CERRAR PRODUCCIÓN
                // -------------------------------------------------------
                const included = (production.outputs || []).filter((o: any) => o.include_in_cost !== false);

                const totalOutput = included.reduce(
                    (sum: number, o: any) => sum + toNum(o.current_performance),
                    0
                );

                const totalCostProd = toNum(production.total_cost);
                const unitCost = (totalOutput > this.EPS) ? this.to6(totalCostProd / totalOutput) : 0;

                tx.update(
                    firestore.collection(`accounts_abc/${accountKey}/${collection}`).doc(production.$key),
                    {
                        status: 'closed',
                        production_date: this.ts(),
                        final_adjustments: finalAdjustments,
                        total_cost: totalCostProd,
                        unit_cost: unitCost,
                        closed_by: user,
                        expiration_date: production?.expiration_date ?? null,
                        labels: production.labels,
                        disposable_items: production.disposable_items,
                        outputs: production.outputs
                    }
                );
            });

            const prodRef = firestore.collection(`accounts_abc/${accountKey}/${collection}`).doc(production.$key);
            const prodDoc = await prodRef.get();
            return { done: true, data: { $key: prodDoc.id, ...prodDoc.data() } };

        } catch (error) {
            console.error('Error en completeProduction:', error);
            throw error;
        }
    }

    async createPurchaseOrder(order: any, items: any) {

        try {

            let batch = this.firestore.firestore.batch();
            let operationsCount = 0;
            const order_key = this.firestore.createId();
            order.user = this.auth.user_resume;
            order.status = 'pending';
            order.creation_date = this.ts();

            batch.set(this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/purchase_orders`).doc(order_key), order);
            operationsCount++;

            const historyPromises: any[] = [];

            for (let item of items) {
                console.log(item)
                const item_key = this.firestore.createId();
                item.order_key = order_key;
                item.creation_date = this.ts();

                batch.set(this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/purchase_order_items`).doc(item_key), item);
                operationsCount++;

                if (operationsCount >= MAX_BATCH_SIZE) {
                    await batch.commit();
                    batch = this.firestore.firestore.batch();
                    operationsCount = 0;
                }
            }

            if (operationsCount > 0) {
                await batch.commit();
            }
            await Promise.all(historyPromises);
            console.log('Orden de compra creada con éxito');
            return true;
        } catch (error) {
            console.error('Error al crear orden de compra:', error);
            throw error;
        }
    }

    async moveFromTrailToMaterials(material: any) {

        try {
            const sampleCollection = this.firestore.collection(`accounts_abc/${this.auth.account_key}/sample_recipes`).doc(material.$key).ref;
            const docSnapshot = await sampleCollection.get();

            if (!docSnapshot?.exists) {
                console.error('El documento no existe.');
                return;
            }

            const data: any = docSnapshot.data();

            await this.firestore.collection(`accounts_abc/${this.auth.account_key}/materials`).doc(material.$key).set({
                ...data,
                type: 'semi-finished'
            });

            await sampleCollection.delete();
            console.log('✅ Receta aprobada  con éxito.');
            return true;
        } catch (error) {
            console.error('❌ Error al mover el documento:', error);
            throw error
        }

    }

    async updatePurchaseOrder(order: any, items: any[]) {
        try {
            const db = this.firestore.firestore;
            const order_key = order.$key;
            const itemsRef = db.collection(`accounts_abc/${this.auth.account_key}/purchase_order_items`);
            const orderRef = db.collection(`accounts_abc/${this.auth.account_key}/purchase_orders`).doc(order_key);

            const { $key, status, user, creation_date, ...orderData } = order;
            await orderRef.update({
                ...orderData,
                updated_date: this.ts(),
                updated_by: this.auth.user_resume
            });

            const previousSnapshot = await itemsRef.where('order_key', '==', order_key).get();
            const previousItems = previousSnapshot.docs.map(doc => ({ $key: doc.id, ...doc.data() }));

            const currentItemKeys = new Set(items.filter(i => i.$key).map(i => i.$key));

            const deletePromises = previousItems
                .filter(prevItem => !currentItemKeys.has(prevItem.$key))
                .map(item => itemsRef.doc(item.$key).delete());

            const upsertPromises = items.map(item => {
                item.order_key = order_key;
                item.creation_date = this.ts();

                if (item.$key) {
                    const prev: any = previousItems.find(p => p.$key === item.$key);
                    if (!prev) return null;
                    if (item.amount_needed !== prev.amount_needed) {
                        const { $key, area, amount_purchased, purchase_price, creation_date, ...data } = item;
                        return itemsRef.doc($key).update({
                            ...data,
                            updated_date: this.ts()
                        });
                    }
                    return null;
                } else {
                    const newKey = this.firestore.createId();
                    return itemsRef.doc(newKey).set(item);
                }
            });

            await Promise.all([...deletePromises, ...upsertPromises]);

            console.log('Orden de compra actualizada con éxito usando Promise.all');
            return true;

        } catch (error) {
            console.error('Error al actualizar orden con Promise.all:', error);
            throw error;
        }
    }

    async completePurchaseOrder(order: any, items: any[], files: string[]) {
        try {
            const accountKey = this.auth.account_key;
            const firestore = this.firestore.firestore;
            const user = this.auth.user_resume;
            const purchaseKey = this.firestore.createId();

            await firestore.runTransaction(async (tx) => {
                let totalPurchaseAmount = 0;
                const plans: any[] = [];

                for (const item of items) {
                    const material = item.material;
                    const area = item.area;
                    const locKey = order.location_key;
                    const areaKey = area.$key;
                    const materialKey = material.$key;

                    const invRef = firestore.doc(`accounts_abc/${accountKey}/inventory/${materialKey}_${locKey}_${areaKey}`);
                    const costRef = firestore.doc(`accounts_abc/${accountKey}/inventory_cost/${materialKey}_${locKey}`);
                    const orderItemRef = firestore.doc(`accounts_abc/${accountKey}/purchase_order_items/${item.$key}`);

                    const [invSnap, costSnap] = await Promise.all([tx.get(invRef), tx.get(costRef)]);

                    const invUnit = material.metric?.symbol || 'pzs';

                    const qtyBase = Number(this.abc.convertMeasure(
                        material.measure,
                        Number(item.amount_purchased),
                        item.purchase_measure?.symbol,
                        invUnit
                    ));

                    if (!(qtyBase > this.EPS)) {
                        throw new Error(`Cantidad inválida para la compra de ${material.name}.`);
                    }

                    const unitConversion = (item.purchase_measure?.symbol === invUnit)
                        ? 1
                        : Number(this.abc.convertMeasureRaw(
                            material.measure,
                            1,
                            item.purchase_measure?.symbol,
                            invUnit
                        ));

                    const unitPrice = unitConversion > 0
                        ? Number(item.purchase_price || 0) / unitConversion
                        : Number(item.purchase_price || 0);



                    totalPurchaseAmount += this.to6(item.purchase_price * Number(item.amount_purchased));

                    const prevAvail = invSnap.exists
                        ? this.to6(Number((invSnap.data() as any)?.available || 0))
                        : 0;


                    const qtyBase6 = this.to6(qtyBase);
                    const newAvail = this.to6(prevAvail + qtyBase6);

                    const costExists = costSnap.exists;
                    const prevPool = costExists ? Number(((costSnap.data() as any)['qty_pool']) || 0) : 0;
                    const prevAvg = costExists ? Number(((costSnap.data() as any)['avg_price']) || 0) : 0;

                    const batchId = this.firestore.createId();
                    const batchRef = invRef.collection('batches').doc(batchId);
                    const batchCode = `PUR-${new Date().toISOString().slice(2, 10).replace(/-/g, '')}-${batchId.slice(-4)}`;

                    const batchData = {
                        batch_key: batchId,
                        entry_date: this.ts(),
                        expiration_date: item.expiration_date ?? null,
                        quantity: qtyBase,
                        unit_price: this.to6(unitPrice),
                        status: 'active',
                        created_from: 'purchase',
                        source_key: purchaseKey,
                        batch_code: batchCode
                    };

                    const logDetailBase = {
                        batch_key: batchId,
                        batch_code: batchCode,
                        unit_price: unitPrice,
                        purchase_quantity: Number(item.amount_purchased),
                        purchase_price: item.purchase_price,
                        purchase_metric: item.purchase_measure ?? null,
                        expiration_date: item.expiration_date ?? (item.expiration_days != null ? Number(item.expiration_days) : null)
                    };

                    plans.push({
                        item,
                        material,
                        areaKey,
                        locKey,
                        invRef,
                        invSnapExists: invSnap.exists,
                        prevAvail,
                        newAvail,
                        batchRef,
                        batchData,
                        wac_prevPool: prevPool,
                        wac_prevAvg: prevAvg,
                        unitPrice,
                        costRef,
                        costExists,
                        orderItemRef,
                        invUnit,
                        logDetailBase
                    });
                }

                // --------------------------------------------------------
                // Purchase Data ya con total calculado
                // --------------------------------------------------------
                const total_paid = order.payments.reduce(
                    (acc: number, payment: any) => acc + Number(payment.amount || 0),
                    0
                );
                const status = (totalPurchaseAmount - total_paid !== 0) ? 'pending' : 'completed';

                const purchaseData = {
                    creation_date: this.ts(),
                    location_key: order.location_key,
                    supplier: order.supplier,
                    payments: order.payments,
                    total: totalPurchaseAmount,
                    total_purchase: totalPurchaseAmount,
                    user,
                    order_key: order.$key,
                    voucher: '',
                    status
                };

                // --------------------------------------------------------
                // Escrituras principales
                // --------------------------------------------------------
                tx.set(this.firestore.collection(`accounts_abc/${accountKey}/purchases`).doc(purchaseKey).ref, purchaseData);
                tx.update(this.firestore.collection(`accounts_abc/${accountKey}/purchase_orders`).doc(order.$key).ref, { status: 'completed', total_purchased: order.total_purchased });

                for (const p of plans) {
                    const item = p.item;
                    if (item.$key) {
                        const { $key, expiration_date, ...data } = item;
                        tx.update(p.orderItemRef, data);
                    }
                }

                for (const p of plans) {
                    if (p.invSnapExists) {
                        tx.update(p.invRef, { available: p.newAvail, last_purchase: this.ts() });
                    } else {
                        tx.set(p.invRef, {
                            material_key: p.material.$key,
                            name: p.material.name,
                            sku: p.material.sku ?? '',
                            area_key: p.areaKey,
                            location_key: p.locKey,
                            available: p.newAvail,
                            metric: p.material.metric ?? null,
                            measure: p.material.measure ?? null,
                            type: p.material.type ?? null,
                            category_key: p.material.category_key ?? null,
                            account: accountKey,
                            creation_date: this.ts(),
                            last_purchase: this.ts()
                        });
                    }

                    tx.set(p.batchRef, p.batchData);

                    const entryRes = this.applyWacCoreWriteOnly(
                        tx,
                        p.costRef,
                        p.material.$key, p.locKey,
                        p.costExists,
                        p.wac_prevPool, p.wac_prevAvg,
                        p.batchData.quantity,
                        p.unitPrice,
                        'purchase',
                        purchaseKey
                    );

                    this.logInventoryMovementBatch(tx, {
                        type: 'purchase',
                        source_key: purchaseKey,
                        material: p.material,
                        quantity_before: p.prevAvail,
                        quantity_after: p.newAvail,
                        quantity_changed: p.newAvail - p.prevAvail,
                        measure: p.invUnit ?? 'pzs',
                        location_key: p.locKey,
                        area_key: p.areaKey,
                        user,
                        detail: {
                            ...p.logDetailBase,
                            avg_price_location_before: entryRes.prevAvg,
                            avg_price_location_after: entryRes.newAvg,
                            qty_pool_before: entryRes.prevPool,
                            qty_pool_after: entryRes.newPool,
                            last_unit_price: entryRes.appliedUnit
                        }
                    });
                }
            });

            // ----------------- Archivos adjuntos -----------------
            if (files?.length) {
                const purchase_receipts_url = await this.uploadFileToCloudinary(files, 'purchase_receipts');
                await this.firestore
                    .collection(`accounts_abc/${accountKey}/purchases`)
                    .doc(purchaseKey)
                    .update({ purchase_receipts_url });
            }

            console.log('✅ Compra completada con éxito (transaccional).');
            return true;

        } catch (error) {
            console.error('❌ Error en completePurchaseOrder:', error);
            throw error;
        }
    }

    chunkArray(array: any[], size: number): any[][] {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
            chunks.push(array.slice(i, i + size));
        }
        return chunks;
    }

    addProductionOrder(order: any, materials: any) {
        return new Promise(async (resolve, reject) => {
            try {
                let batch = this.firestore.firestore.batch();

                let operationsCount = 0;

                // for (let production of products) {
                //     let production_key = this.firestore.createId();
                //     production.creation_date = new Date();
                //     production.material_key = production.$key;
                //     production.status = 'on_hold'
                //     production.user = this.auth.user_resume;
                //     production.location_key = this.auth.location_key;
                //     production.area_key = this.auth.area_key;
                //     delete production.$key;
                console.log(materials)
                let productionOrder_key = this.firestore.createId();

                batch.set(this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/production_orders`).doc(productionOrder_key), { ...order, materials: materials, creation_date: this.ts(), status: 'on_hold' });
                operationsCount++;
                if (operationsCount >= MAX_BATCH_SIZE) {
                    await batch.commit();
                    batch = this.firestore.firestore.batch();
                    operationsCount = 0;
                }
                // };

                await batch.commit();
                resolve(productionOrder_key);
            } catch (error) {
                reject(error);
            }

        })
    }

    async createTransferRequest(requestData: any, signature: any, materials: any[]) {

        try {
            const requestKey = this.firestore.createId();

            // 1) Subir firma (opcional)
            let signatureUrl: string | null = null;
            if (signature) {
                const folder = `signatures/${this.auth.account_key}`;
                signatureUrl = await this.cloudinary.uploadImage(signature, folder);
            }

            // 2) Ref correcta
            const reqRef = this.firestore.firestore
                .collection(`accounts_abc/${this.auth.account_key}/transfer_requests`)
                .doc(requestKey);

            // 3) Items normalizados
            const items = (materials || [])
                .map(p => ({
                    material_key: p.material.material_key,
                    name: p.material.name,
                    qty: Number(p.amount_requested || 0),
                    metric: p.material.metric || null,
                    measure: p.material.measure || null,
                    sku: p.material.sku || null,
                    type: p.material.type || null,
                    category: p.material.category || null,
                }))
                .filter(x => x.material_key && x.qty > 0);

            if (items.length === 0) {
                throw new Error('Debes enviar al menos un material con cantidad válida.');
            }

            // 4) Guardar solicitud
            await reqRef.set({
                $key: requestKey,
                type: 'transfer_request',

                // Estado actual
                status: 'requested',

                // Datos base
                from_location: requestData.requested_by_location,
                from_area: requestData.requested_by_area,
                to_location: requestData.requested_to_location,
                to_area: requestData.requested_to_area,
                materials: items,
                required_date: requestData.required_date || null,
                is_emergency_request: !!requestData.is_emergency_request,
                signature_url: signatureUrl,
                transfer_key: null,
                user: this.auth.user_resume,

                // Trazabilidad principal
                requested_at: this.ts(),
                requested_by: this.auth.user_resume,

                approved_at: null,
                approved_by: null,

                preparing_at: null,
                preparing_by: null,

                ready_at: null,
                ready_by: null,

                sent_at: null,
                sent_by: null,

                received_at: null,
                received_by: null,

                rejected_at: null,
                rejected_by: null,
                rejection_reason: null,

                cancelled_at: null,
                cancelled_by: null,
                cancellation_reason: null,

                // Historial de estados
                status_history: [
                    {
                        status: 'requested',
                        date: new Date(),
                        user: this.auth.user_resume,
                        note: requestData.is_emergency_request
                            ? 'Solicitud creada como requisición de emergencia'
                            : 'Solicitud creada'
                    }
                ],

                created_at: this.ts(),
                updated_at: this.ts(),
            });

            console.log('Solicitud de transferencia creada con éxito.');

            // Enviar notificación a la ubicación destino
            try {
                await this.notificationService.sendNotification({
                    type: 'transfer_request',
                    status: 'unread',
                    title: 'Nueva solicitud de transferencia',
                    message: `${requestData.requested_by_location.name} ha solicitado una transferencia`,
                    target_location_key: requestData.requested_to_location.$key,
                    target_area_key: requestData.requested_to_area?.$key || null,
                    source_location_key: requestData.requested_by_location.$key,
                    source_area_key: requestData.requested_by_area.$key,
                    reference_key: requestKey,
                    reference_type: 'transfer_request'
                });
            } catch (e) {
                console.error('Error al enviar notificación:', e);
            }

            return { request_key: requestKey };

        } catch (error) {
            console.error('Error al crear solicitud de transferencia:', error);
            throw error;
        }
    }

    async approveTransferRequest(request: any) {
        try {
            const ref = this.firestore.firestore
                .collection(`accounts_abc/${this.auth.account_key}/transfer_requests`)
                .doc(request.$key);

            await ref.update({
                status: 'approved',
                approved_at: this.ts(),
                approved_by: this.auth.user_resume,
                updated_at: this.ts(),
            });

            return true;
        } catch (error) {
            console.error('Error al aprobar requisición:', error);
            throw error;
        }
    }

    async markTransferRequestPreparing(request: any) {
        try {
            const ref = this.firestore.firestore
                .collection(`accounts_abc/${this.auth.account_key}/transfer_requests`)
                .doc(request.$key);

            await ref.update({
                status: 'preparing',
                preparing_at: this.ts(),
                preparing_by: this.auth.user_resume,
                updated_at: this.ts(),
            });

            return true;
        } catch (error) {
            console.error('Error al marcar requisición en preparación:', error);
            throw error;
        }
    }

    async markTransferRequestReady(request: any, materials: any[]) {
        try {
            const accountKey = this.auth.account_key;
            const movementLogs: any[] = [];

            if (!request?.$key) {
                throw new Error('La requisición no es válida.');
            }

            if (!request?.to_location?.$key || !request?.to_area?.$key) {
                throw new Error('La requisición no tiene ubicación/área origen válidas.');
            }

            const requestRef = this.firestore.firestore
                .collection(`accounts_abc/${accountKey}/transfer_requests`)
                .doc(request.$key);

            const isSameLocation = request.to_location.$key === request.from_location.$key;

            const enrichedMaterials: any[] = [];

            for (const material of (materials || [])) {
                const amount = Number(material.amount_to_transfer || 0);

                if (amount <= 0) {
                    throw new Error(`Debes indicar una cantidad válida a transferir para ${material.name}.`);
                }

                // buscar inventario ORIGEN por (material_key + to_location + to_area)
                const invSnap = await this.firestore.firestore
                    .collection(`accounts_abc/${accountKey}/inventory`)
                    .where('location_key', '==', request.to_location.$key)
                    .where('area_key', '==', request.to_area.$key)
                    .where('material_key', '==', material.material_key)
                    .limit(1)
                    .get();

                if (invSnap.empty) {
                    throw new Error(`Inventario no encontrado para ${material.name} en el área de origen.`);
                }

                const originRef = invSnap.docs[0].ref;
                const originId = invSnap.docs[0].id;

                let enrichedMaterial: any = null;

                await this.firestore.firestore.runTransaction(async (tx: any) => {
                    const originDoc = await tx.get(originRef);
                    if (!originDoc.exists) throw new Error(`Inventario origen no existe (${originId}).`);

                    const originData: any = originDoc.data();

                    const beforeOrigin = Number(originData.available || 0);
                    const afterOrigin = this.to6(beforeOrigin - amount);

                    if (afterOrigin < -this.EPS) {
                        throw new Error(`No hay suficiente ${originData.name} en el inventario de origen.`);
                    }

                    // inventory_cost (por location)
                    const costRef = this.firestore.firestore.doc(
                        this.costDocPath(originData.material_key, originData.location_key)
                    );

                    const costSnap = await tx.get(costRef);

                    let avgPriceLocation = 0;
                    if (costSnap.exists) {
                        const costData: any = costSnap.data();
                        avgPriceLocation = Number(costData.avg_price || 0);
                    }

                    // FIFO batches
                    let remaining = amount;
                    let totalCost = 0;
                    const usedBatches: any[] = [];

                    const fromBatchesSnap = await originRef.collection('batches')
                        .where('status', '==', 'active')
                        .orderBy('entry_date', 'asc')
                        .get();

                    for (const doc of fromBatchesSnap.docs) {
                        if (remaining <= this.EPS) break;

                        const data: any = { ...doc.data(), batch_key: doc.id };
                        const qty = Number(data.quantity || 0);
                        if (qty <= this.EPS) continue;

                        const transferQty = Math.min(qty, remaining);
                        const cost = transferQty * Number(data.unit_price || 0);

                        remaining = this.to6(remaining - transferQty);
                        totalCost += cost;

                        const newQty = this.to6(qty - transferQty);
                        const batchUpdate: any = {
                            quantity: newQty,
                            status: newQty <= this.EPS ? 'depleted' : 'active',
                        };

                        if (newQty <= this.EPS) {
                            batchUpdate.depleted_date = this.ts();
                        }

                        tx.update(doc.ref, batchUpdate);

                        usedBatches.push({
                            batch_key: data.batch_key,
                            batch_code: data.batch_code,
                            transferred_quantity: Number(transferQty.toFixed(4)),
                            unit_price: Number(data.unit_price || 0),
                            entry_date: data.entry_date,
                            expiration_date: data.expiration_date || null,
                        });
                    }

                    if (remaining > this.EPS) {
                        throw new Error(`No hay suficientes lotes activos para transferir ${originData.name}.`);
                    }

                    // fallback avg solo para log si no existe cost
                    if (!costSnap.exists && amount > this.EPS) {
                        avgPriceLocation = Number((totalCost / amount).toFixed(6));
                    }

                    // actualizar inventory origen (área)
                    tx.update(originRef, { available: afterOrigin });

                    // SOLO cross-location: descargar qty_pool del inventory_cost del ORIGEN
                    if (!isSameLocation) {
                        const exists = costSnap.exists;
                        const prevPool = exists ? Number((costSnap.data() as any)?.qty_pool || 0) : 0;
                        const prevAvg = exists ? Number((costSnap.data() as any)?.avg_price || 0) : avgPriceLocation;

                        if (prevPool - amount < -this.EPS) {
                            throw new Error(
                                `qty_pool insuficiente en inventory_cost para ${originData.name}. pool=${prevPool}, out=${amount}`
                            );
                        }

                        this.applyWacExitCoreWriteOnly(
                            tx,
                            costRef,
                            originData.material_key,
                            originData.location_key,
                            exists,
                            prevPool,
                            prevAvg,
                            amount,
                            'transfer_request_ready',
                            String(request.$key)
                        );
                    }

                    enrichedMaterial = {
                        material_key: material.material_key,
                        name: material.name,
                        sku: material.sku || null,
                        qty: Number(material.qty || 0), // solicitado
                        amount_requested: Number(material.qty || 0),
                        amount_to_transfer: amount,
                        amount_transferred: amount,
                        metric: material.metric || null,
                        measure: material.measure || null,
                        type: material.type || null,
                        used_batches: usedBatches,
                        avg_price_location: avgPriceLocation,
                    };

                    movementLogs.push({
                        type: 'transfer_request_ready_out',
                        source_key: request.$key,
                        material: originData,
                        quantity_before: beforeOrigin,
                        quantity_after: afterOrigin,
                        quantity_changed: -amount,
                        measure: originData.metric?.symbol,
                        location_key: originData.location_key,
                        area_key: originData.area_key,
                        user: this.auth.user_resume,
                        avg_price_location: avgPriceLocation,
                        detail: {
                            direction: 'out',
                            request_status: 'ready',
                            to_location: request.from_location.$key,
                            to_area: request.from_area.$key,
                            used_batches: usedBatches,
                            total_cost: Number(totalCost.toFixed(6)),
                        }
                    });
                });

                enrichedMaterials.push(enrichedMaterial);
            }

            await requestRef.update({
                status: 'ready',
                materials: enrichedMaterials,
                ready_at: this.ts(),
                ready_by: this.auth.user_resume,
                inventory_discounted_at: this.ts(),
                inventory_discounted_by: this.auth.user_resume,
                updated_at: this.ts(),
            });

            const batch = this.firestore.firestore.batch();
            for (const movement of movementLogs) {
                this.logInventoryMovementBatch(batch, movement);
            }
            await batch.commit();

            return true;

        } catch (error) {
            console.error('Error al marcar requisición lista:', error);
            throw error;
        }
    }

    async sendTransferFromRequest(request: any, userSignature: any, materials: any[], send_from_area: any, deliveryDetails: any, deliverySignature: any
    ) {
        try {
            const accountKey = this.auth.account_key;

            if (!send_from_area?.$key) {
                throw new Error('Debes seleccionar el área que envía.');
            }

            // 1) subir firma
            const folder = `signatures/${accountKey}`;
            const userSignatureUrl = await this.cloudinary.uploadImage(userSignature, folder);
            const deliverySignatureUrl = await this.cloudinary.uploadImage(deliverySignature, folder);

            // 2) refs
            const transferKey = this.firestore.createId();

            const transferRef = this.firestore.firestore
                .collection(`accounts_abc/${accountKey}/transfers`)
                .doc(transferKey);

            const requestRef = this.firestore.firestore
                .collection(`accounts_abc/${accountKey}/transfer_requests`)
                .doc(request.$key);

            const now = this.ts();

            // 3) transaction: validar ready + crear transfer + actualizar requisición
            await this.firestore.firestore.runTransaction(async (tx: any) => {
                const reqSnap = await tx.get(requestRef);
                if (!reqSnap.exists) {
                    throw new Error('La solicitud ya no existe.');
                }

                const reqData: any = reqSnap.data();

                if (reqData.transfer_key) {
                    throw new Error('Esta solicitud ya tiene una transferencia asociada.');
                }

                if (reqData.status !== 'ready') {
                    throw new Error('La requisición debe estar en estado lista para enviarse.');
                }

                const transferDoc: any = {
                    $key: transferKey,
                    type: 'transfer',
                    request_id: request.$key,

                    status: 'in_transit',
                    in_transit_at: now,
                    in_transit_by: this.auth.user_resume || this.auth.user,

                    delivery_details: {
                        delivered_by_name: deliveryDetails?.delivered_by_name || null,
                        delivered_by_document: deliveryDetails?.delivered_by_document || null,
                        delivered_by_phone: deliveryDetails?.delivered_by_phone || null,
                        delivery_signature_url: deliverySignatureUrl
                    },

                    in_transit_details: {
                        delivered_by_user: this.auth.user_resume,
                        delivered_by_user_signature: userSignatureUrl,
                        in_transit_at: now,
                    },

                    from_location: reqData.to_location,
                    from_area: send_from_area,

                    to_location: reqData.from_location,
                    to_area: reqData.from_area,

                    materials: (reqData.materials || []).map((m: any) => ({
                        material_key: m.material_key,
                        name: m.name,
                        sku: m.sku || null,

                        amount_requested: Number(m.amount_requested || m.qty || 0),
                        amount_to_transfer: Number(m.amount_to_transfer || 0),
                        amount_transferred: Number(m.amount_transferred || m.amount_to_transfer || 0),

                        metric: m.metric || null,
                        measure: m.measure || null,
                        type: m.type || null,

                        used_batches: m.used_batches || [],
                        avg_price_location: m.avg_price_location ?? null,
                    })),

                    received_details: null,
                    rejection_details: null,

                    created_at: now,
                    updated_at: now,
                };

                tx.set(transferRef, transferDoc);

                tx.update(requestRef, {
                    status: 'in_transit',
                    transfer_key: transferKey,
                    delivery_details: {
                        delivered_by_name: deliveryDetails?.delivered_by_name || null,
                        delivered_by_document: deliveryDetails?.delivered_by_document || null,
                        delivered_by_phone: deliveryDetails?.delivered_by_phone || null,
                        delivery_signature_url: deliverySignatureUrl
                    },
                    in_transit_at: now,
                    in_transit_by: this.auth.user_resume,
                    in_transit_signature_url: userSignatureUrl,
                    updated_at: now,
                });
            });

            // 5) notificar al solicitante
            try {
                await this.notificationService.sendNotification({
                    type: 'transfer',
                    status: 'unread',
                    title: 'Tu solicitud de transferencia ha sido enviada',
                    message: `${request.to_location.name} ha enviado los materiales que solicitaste`,
                    target_location_key: request.from_location.$key,
                    target_area_key: request.from_area.$key,
                    source_location_key: request.to_location.$key,
                    source_area_key: send_from_area.$key,
                    reference_key: transferKey,
                    reference_type: 'transfer'
                });
            } catch (e) {
                console.error('Error al enviar notificación:', e);
            }

            return true;

        } catch (error) {
            console.error('Error al enviar transferencia desde solicitud:', error);
            throw error;
        }
    }

    async rejectRequestTransfer(request: any, signature: any, reason: string) {
        try {
            const accountKey = this.auth.account_key;
            const folder = `signatures/${accountKey}`;
            const signatureUrl = await this.cloudinary.uploadImage(signature, folder);

            const rejection_request_details = {
                rejected_by: this.auth.user_resume,
                rejected_date: this.ts(),
                rejected_reason: reason,
                rejected_signature: signatureUrl,
                rejected_from_status: request.status,
            }

            await this.firestore.firestore
                .collection(`accounts_abc/${accountKey}/transfer_requests`)
                .doc(request.$key)
                .update({
                    status: 'rejected',
                    rejection_request_details
                });

            // notificar al solicitante que su solicitud fue rechazada
            await this.notificationService.sendNotification({
                type: 'transfer_request_rejected',
                status: 'unread',
                title: 'Solicitud rechazada',
                message: `${request.to_location.name} ha rechazado tu solicitud. Motivo: ${reason}`,
                target_location_key: request.from_location.$key,
                target_area_key: request.from_area.$key,
                source_location_key: request.to_location.$key,
                source_area_key: request.to_area?.$key || null,
                reference_key: request.$key,
                reference_type: 'transfer_request'
            });

            return true;
        } catch (error) {
            console.error('Error al devolver materiales al inventario:', error);
            throw error;
        }
    }

    async cancelTransferRequest(request: any, signature: any, reason: string) {
        try {
            const accountKey = this.auth.account_key;
            const firestore = this.firestore.firestore;
            const user = this.auth.user_resume;

            if (!['requested', 'approved', 'preparing', 'ready'].includes(request.status)) {
                throw new Error('Esta requisición ya no puede cancelarse.');
            }

            if (request.transfer_key || request.status === 'in_transit') {
                throw new Error('Esta requisición ya fue enviada y no puede cancelarse.');
            }

            const folder = `signatures/${accountKey}`;
            const signatureUrl = await this.cloudinary.uploadImage(signature, folder);

            const requestRef = firestore
                .collection(`accounts_abc/${accountKey}/transfer_requests`)
                .doc(request.$key);

            const isReady = request.status === 'ready';
            const isSameLocation = request.to_location.$key === request.from_location.$key;

            let readyMovements: any[] = [];

            if (isReady) {
                const movementsSnap = await firestore
                    .collection(`accounts_abc/${accountKey}/inventory_movements`)
                    .where('source_key', '==', request.$key)
                    .where('type', '==', 'transfer_request_ready_out')
                    .get();

                if (movementsSnap.empty) {
                    throw new Error('No se encontraron movimientos para revertir esta requisición.');
                }

                readyMovements = movementsSnap.docs.map((doc: any) => ({
                    $key: doc.id,
                    ...doc.data()
                }));
            }

            await firestore.runTransaction(async (tx: any) => {
                const reqSnap = await tx.get(requestRef);

                if (!reqSnap.exists) {
                    throw new Error('La requisición no existe.');
                }

                const reqData: any = reqSnap.data();

                if (reqData.status !== request.status) {
                    throw new Error('La requisición cambió de estado. Recarga e intenta de nuevo.');
                }

                if (reqData.transfer_key || reqData.status === 'in_transit') {
                    throw new Error('Esta requisición ya fue enviada y no puede cancelarse.');
                }

                const plans: any[] = [];

                // 1. SOLO LECTURAS
                if (isReady) {
                    for (const movement of readyMovements) {
                        const materialKey = movement.material_key;
                        const locationKey = movement.location_key;
                        const areaKey = movement.area_key;

                        const inventoryRef = firestore
                            .collection(`accounts_abc/${accountKey}/inventory`)
                            .doc(`${materialKey}_${locationKey}_${areaKey}`);

                        const invSnap = await tx.get(inventoryRef);

                        if (!invSnap.exists) {
                            throw new Error(`Inventario no encontrado para revertir ${movement.material_name}.`);
                        }

                        const batchReads: any[] = [];

                        for (const usedBatch of movement.detail?.used_batches || []) {
                            const batchKey = usedBatch.batch_key;
                            if (!batchKey) continue;

                            const lotRef = inventoryRef.collection('batches').doc(batchKey);
                            const lotSnap = await tx.get(lotRef);

                            batchReads.push({
                                usedBatch,
                                lotRef,
                                lotSnap
                            });
                        }

                        let costRef: any = null;
                        let costSnap: any = null;

                        if (!isSameLocation) {
                            costRef = firestore.doc(this.costDocPath(materialKey, locationKey));
                            costSnap = await tx.get(costRef);
                        }

                        plans.push({
                            movement,
                            materialKey,
                            locationKey,
                            areaKey,
                            inventoryRef,
                            invSnap,
                            batchReads,
                            costRef,
                            costSnap
                        });
                    }
                }

                // 2. SOLO ESCRITURAS
                for (const plan of plans) {
                    const {
                        movement,
                        materialKey,
                        locationKey,
                        areaKey,
                        inventoryRef,
                        invSnap,
                        batchReads,
                        costRef,
                        costSnap
                    } = plan;

                    const invData: any = invSnap.data();

                    const qtyToReturn = this.to6(Math.abs(Number(movement.quantity_changed || 0)));

                    if (qtyToReturn <= this.EPS) continue;

                    const beforeAvail = Number(invData.available || 0);
                    const afterAvail = this.to6(beforeAvail + qtyToReturn);

                    tx.update(inventoryRef, {
                        available: afterAvail,
                        updated_at: this.ts()
                    });

                    const returnedBatches: any[] = [];

                    for (const item of batchReads) {
                        const { usedBatch, lotRef, lotSnap } = item;

                        if (!lotSnap.exists) continue;

                        const qty = this.to6(Number(usedBatch.transferred_quantity || 0));
                        if (qty <= this.EPS) continue;

                        const lotData: any = lotSnap.data();
                        const beforeBatchQty = Number(lotData.quantity || 0);
                        const afterBatchQty = this.to6(beforeBatchQty + qty);

                        tx.update(lotRef, {
                            quantity: afterBatchQty,
                            status: 'active',
                            depleted_date: null,
                            reactivated_at: this.ts()
                        });

                        returnedBatches.push({
                            batch_key: usedBatch.batch_key,
                            batch_code: usedBatch.batch_code || null,
                            quantity_returned: qty,
                            unit_price: Number(usedBatch.unit_price || 0),
                            quantity_before: beforeBatchQty,
                            quantity_after: afterBatchQty
                        });
                    }

                    if (!isSameLocation && costRef && costSnap) {
                        const costExists = costSnap.exists;
                        const prevPool = costExists ? Number((costSnap.data() as any)?.qty_pool || 0) : 0;
                        const prevAvg = costExists ? Number((costSnap.data() as any)?.avg_price || 0) : 0;

                        this.applyWacCoreWriteOnly(
                            tx,
                            costRef,
                            materialKey,
                            locationKey,
                            costExists,
                            prevPool,
                            prevAvg,
                            qtyToReturn,
                            prevAvg,
                            'transfer_request_cancel_return',
                            request.$key
                        );
                    }

                    this.logInventoryMovementBatch(tx, {
                        type: 'transfer_request_cancel_return',
                        source_key: request.$key,
                        material: {
                            material_key: materialKey,
                            name: movement.material_name,
                            sku: movement.sku || '',
                            metric: invData.metric || null,
                            measure: invData.measure || null,
                            type: invData.type || null
                        },
                        quantity_before: beforeAvail,
                        quantity_after: afterAvail,
                        quantity_changed: qtyToReturn,
                        measure: movement.measure || invData.metric?.symbol || null,
                        location_key: locationKey,
                        area_key: areaKey,
                        user,
                        detail: {
                            direction: 'in',
                            reason: 'cancel_ready_transfer_request',
                            cancel_reason: reason,
                            reversal_of: movement.$key,
                            original_type: movement.type,
                            request_status_before_cancel: request.status,
                            returned_batches: returnedBatches,
                            physical_return_required: true
                        }
                    });
                }

                tx.update(requestRef, {
                    status: 'cancelled',
                    cancelled_from_status: request.status,
                    cancellation_details: {
                        cancelled_by: user,
                        cancelled_date: this.ts(),
                        cancelled_reason: reason,
                        cancelled_signature: signatureUrl,
                        cancelled_from_status: request.status,
                        inventory_reversed: isReady
                    },
                    cancelled_at: this.ts(),
                    cancelled_by: user,
                    cancellation_reason: reason,
                    updated_at: this.ts()
                });
            });

            await this.notificationService.sendNotification({
                type: 'transfer_request_cancelled',
                status: 'unread',
                title: 'Solicitud cancelada',
                message: 'El usuario ha cancelado la solicitud',
                target_location_key: request.to_location.$key,
                target_area_key: request.to_area?.$key || null,
                source_location_key: request.from_location.$key,
                source_area_key: request.from_area?.$key || null,
                reference_key: request.$key,
                reference_type: 'transfer_request'
            });

            return true;
        } catch (error) {
            console.error('Error al cancelar requisición:', error);
            throw error;
        }
    }

    async sendTransfer(transferData: any, userSignature: any, materials: any[], deliveryDetails: any, deliverySignature: any
    ) {
        try {
            const accountKey = this.auth.account_key;
            const now = this.ts();
            const user = this.auth.user_resume || this.auth.user;

            if (!transferData?.from_location?.$key) throw new Error('Selecciona ubicación origen.');
            if (!transferData?.from_area?.$key) throw new Error('Selecciona área origen.');
            if (!transferData?.to_location?.$key) throw new Error('Selecciona ubicación destino.');
            if (!transferData?.to_area?.$key) throw new Error('Selecciona área destino.');

            if (!Array.isArray(materials) || materials.length === 0) {
                throw new Error('Debes agregar materiales para transferir.');
            }

            const folder = `signatures/${accountKey}`;
            const userSignatureUrl = await this.cloudinary.uploadImage(userSignature, folder);
            const deliverySignatureUrl = await this.cloudinary.uploadImage(deliverySignature, folder);

            const transferKey = this.firestore.createId();

            const transferRef = this.firestore.firestore
                .collection(`accounts_abc/${accountKey}/transfers`)
                .doc(transferKey);

            const transferDoc: any = {
                $key: transferKey,
                type: 'transfer',
                status: 'in_transit',
                request_id: null,

                from_location: transferData.from_location,
                from_area: transferData.from_area,
                to_location: transferData.to_location,
                to_area: transferData.to_area,

                in_transit_at: now,
                in_transit_by: user,

                delivery_details: {
                    delivered_by_name: deliveryDetails?.delivered_by_name || null,
                    delivered_by_document: deliveryDetails?.delivered_by_document || null,
                    delivered_by_phone: deliveryDetails?.delivered_by_phone || null,
                    delivery_signature_url: deliverySignatureUrl,
                },

                in_transit_details: {
                    delivered_by_user: user,
                    delivered_by_user_signature: userSignatureUrl,
                    in_transit_at: now,
                },

                received_details: null,
                rejection_details: null,

                created_at: now,
                updated_at: now,

                materials: materials.map((m: any) => {
                    const inv = m.material;
                    const matKey = inv?.material_key || inv?.$key;

                    return {
                        material_key: matKey,
                        name: inv?.name || null,
                        sku: inv?.sku || null,
                        category: inv?.category || null,

                        amount_requested: null,
                        amount_to_transfer: Number(m.amount_to_transfer || 0),
                        amount_transferred: Number(m.amount_to_transfer || 0),

                        metric: inv?.metric || null,
                        measure: inv?.measure || null,
                        type: inv?.type || null,

                        used_batches: [],
                        avg_price_location: null,
                    };
                }),
            };

            await transferRef.set(transferDoc);

            await this.sendTransferOut(transferDoc, userSignatureUrl, 'in_transit');

            try {
                await this.notificationService.sendNotification({
                    type: 'transfer',
                    status: 'unread',
                    title: 'Nueva transferencia en tránsito',
                    message: `${transferData.from_location.name} te ha enviado una transferencia`,
                    target_location_key: transferData.to_location.$key,
                    target_area_key: transferData.to_area.$key,
                    source_location_key: transferData.from_location.$key,
                    source_area_key: transferData.from_area.$key,
                    reference_key: transferKey,
                    reference_type: 'transfer'
                });
            } catch (e) {
                console.error('Error al enviar notificación:', e);
            }

            return true;

        } catch (error) {
            console.error('Error al enviar transferencia:', error);
            throw error;
        }
    }

    async sendTransferOut(transfer: any, signatureUrl?: string, finalStatus: string = 'in_transit') {
        const accountKey = this.auth.account_key;
        const movementLogs: any[] = [];

        const fromLocKey = transfer.from_location.$key;
        const toLocKey = transfer.to_location.$key;
        const isSameLocation = fromLocKey === toLocKey;

        // iterar materiales (FIFO)
        for (const material of transfer.materials) {
            material.amount_to_transfer = Number(material.amount_to_transfer);

            // buscar inventario ORIGEN por (material_key + from_location + from_area)
            const invSnap = await this.firestore.firestore
                .collection(`accounts_abc/${accountKey}/inventory`)
                .where('location_key', '==', fromLocKey)
                .where('area_key', '==', transfer.from_area.$key)
                .where('material_key', '==', material.material_key)
                .limit(1)
                .get();

            if (invSnap.empty) {
                throw new Error(`Inventario no encontrado para ${material.name} en el área de origen.`);
            }

            const originRef = invSnap.docs[0].ref;
            const originId = invSnap.docs[0].id;

            await this.firestore.firestore.runTransaction(async (tx: any) => {
                const originDoc = await tx.get(originRef);
                if (!originDoc.exists) throw new Error(`Inventario origen no existe (${originId}).`);

                const originData: any = originDoc.data();

                const amount = Number(material.amount_to_transfer || 0);
                const beforeOrigin = Number(originData.available || 0);
                const afterOrigin = this.to6(beforeOrigin - amount);

                if (afterOrigin < -this.EPS) {
                    throw new Error(`No hay suficiente ${originData.name} en el inventario de origen.`);
                }

                // inventory_cost (por location)
                const costRef = this.firestore.firestore.doc(
                    this.costDocPath(originData.material_key, originData.location_key)
                );

                const costSnap = await tx.get(costRef);

                let avgPriceLocation = 0;
                if (costSnap.exists) {
                    const costData: any = costSnap.data();
                    avgPriceLocation = Number(costData.avg_price || 0);
                }

                // FIFO batches
                let remaining = amount;
                let totalCost = 0;
                const usedBatches: any[] = [];

                const fromBatchesSnap = await originRef.collection('batches')
                    .where('status', '==', 'active')
                    .orderBy('entry_date', 'asc')
                    .get();

                for (const doc of fromBatchesSnap.docs) {
                    if (remaining <= this.EPS) break;

                    const data: any = { ...doc.data(), batch_key: doc.id };
                    const qty = Number(data.quantity || 0);
                    if (qty <= this.EPS) continue;

                    const transferQty = Math.min(qty, remaining);
                    const cost = transferQty * Number(data.unit_price || 0);

                    remaining = this.to6(remaining - transferQty);
                    totalCost += cost;

                    const newQty = this.to6(qty - transferQty);
                    const batchUpdate: any = {
                        quantity: newQty,
                        status: newQty <= this.EPS ? 'depleted' : 'active',
                    };
                    if (newQty <= this.EPS) batchUpdate.depleted_date = this.ts();

                    tx.update(doc.ref, batchUpdate);

                    usedBatches.push({
                        batch_key: data.batch_key,
                        batch_code: data.batch_code,
                        transferred_quantity: Number(transferQty.toFixed(4)),
                        unit_price: Number(data.unit_price || 0),
                        entry_date: data.entry_date,
                        expiration_date: data.expiration_date || null,
                    });
                }

                if (remaining > this.EPS) {
                    throw new Error(`No hay suficientes lotes activos para transferir ${originData.name}.`);
                }

                // fallback avg solo para log si no existe cost
                if (!costSnap.exists && amount > this.EPS) {
                    avgPriceLocation = Number((totalCost / amount).toFixed(6));
                }

                // set en el material (se guarda luego al update del transfer)
                material.used_batches = usedBatches;
                material.avg_price_location = avgPriceLocation;
                material.amount_transferred = amount;

                // actualizar inventory origen (área)
                tx.update(originRef, { available: afterOrigin });

                // ✅ SOLO cross-location: descargar qty_pool del inventory_cost del ORIGEN (no recalcula avg)
                if (!isSameLocation) {
                    const exists = costSnap.exists;
                    const prevPool = exists ? Number((costSnap.data() as any)?.qty_pool || 0) : 0;
                    const prevAvg = exists ? Number((costSnap.data() as any)?.avg_price || 0) : avgPriceLocation;

                    // Recomendado: no “tapar” inconsistencias
                    if (prevPool - amount < -this.EPS) {
                        throw new Error(
                            `qty_pool insuficiente en inventory_cost para ${originData.name}. pool=${prevPool}, out=${amount}`
                        );
                    }

                    this.applyWacExitCoreWriteOnly(
                        tx,
                        costRef,
                        originData.material_key,
                        originData.location_key,
                        exists,
                        prevPool,
                        prevAvg,
                        amount,
                        'transfer',
                        String(transfer.$key)
                    );
                }

                // log movimiento OUT
                movementLogs.push({
                    type: 'transfer_out',
                    source_key: transfer.$key,
                    material: originData,
                    quantity_before: beforeOrigin,
                    quantity_after: afterOrigin,
                    quantity_changed: -amount,
                    measure: originData.metric?.symbol,
                    location_key: originData.location_key,
                    area_key: originData.area_key,
                    user: this.auth.user_resume,
                    avg_price_location: avgPriceLocation,
                    detail: {
                        direction: 'out',
                        to_location: toLocKey,
                        to_area: transfer.to_area.$key,
                        used_batches: usedBatches,
                        total_cost: Number(totalCost.toFixed(6)),
                    }
                });
            });
        }

        // actualizar transfer con materiales enriquecidos
        const newMaterials = transfer.materials.map((p: any) => ({
            material_key: p.material_key,
            name: p.name,
            sku: p.sku || null,
            amount_requested: Number(p.amount_requested || 0),
            amount_to_transfer: Number(p.amount_to_transfer || 0),
            amount_transferred: Number(p.amount_transferred || 0),
            metric: p.metric || null,
            measure: p.measure || null,
            type: p.type || null,
            used_batches: p.used_batches || [],
            avg_price_location: p.avg_price_location ?? null,
        }));

        await this.firestore.firestore
            .collection(`accounts_abc/${accountKey}/transfers`)
            .doc(transfer.$key)
            .update({
                materials: newMaterials,
                status: finalStatus,
                updated_at: this.ts(),
            });

        // inventory_movements batch
        const batch = this.firestore.firestore.batch();
        for (const movement of movementLogs) {
            this.logInventoryMovementBatch(batch, movement);
        }
        await batch.commit();

        return true;
    }

    async acceptTransfer(transfer: any, signature: any) {
        try {
            const accountKey = this.auth.account_key;
            const firestore = this.firestore.firestore;
            const user = this.auth.user_resume;

            if (!transfer?.$key) throw new Error('Transfer inválida.');
            if (transfer.status !== 'in_transit') throw new Error('Solo se puede aceptar una transferencia en tránsito.');

            // Firma
            const folder = `signatures/${accountKey}`;
            const signatureUrl = await this.cloudinary.uploadImage(signature, folder);

            const transferRef = firestore
                .collection(`accounts_abc/${accountKey}/transfers`)
                .doc(transfer.$key);

            const fromLocKey = transfer.from_location.$key;
            const toLocKey = transfer.to_location.$key;
            const isSameLocation = fromLocKey === toLocKey;

            // 1) Procesar IN por material (usa used_batches)
            for (const item of (transfer.materials || [])) {

                const matKey = item.material_key;
                if (!matKey) continue;

                const usedRaw = Array.isArray(item.used_batches) ? item.used_batches : [];
                if (!usedRaw.length) {
                    throw new Error(`No hay used_batches para ${item.name || matKey}.`);
                }

                const usedBatches = usedRaw
                    .map((b: any) => ({
                        batch_key: b.batch_key,
                        batch_code: b.batch_code,
                        qty: Number(b.used_from_batch ?? b.transferred_quantity ?? b.quantity ?? 0),
                        unit_price: Number(b.unit_price || 0),
                        entry_date: b.entry_date,
                        expiration_date: b.expiration_date || null,
                    }))
                    .filter((u: any) => u.qty > this.EPS);

                const totalQty = this.to6(usedBatches.reduce((s: number, x: any) => s + x.qty, 0));
                const totalCost = Number(usedBatches.reduce((s: number, x: any) => s + x.qty * x.unit_price, 0).toFixed(6));

                const toAreaKey = transfer.to_area.$key;

                const destInvRef = firestore.doc(`accounts_abc/${accountKey}/inventory/${matKey}_${toLocKey}_${toAreaKey}`);
                const destBatchesCol = destInvRef.collection('batches');

                const destCostRef = !isSameLocation ? firestore.doc(this.costDocPath(matKey, toLocKey)) : null as any;

                const destBatchesSnap = await destBatchesCol.get();
                const destBatchRefs = destBatchesSnap.docs.map(d => d.ref);

                await firestore.runTransaction(async (tx) => {
                    // Idempotencia: re-leer transfer
                    const tSnap = await tx.get(transferRef);
                    if (!tSnap.exists) throw new Error('Transfer no existe.');
                    const tData: any = tSnap.data();
                    if (tData.status !== 'in_transit') throw new Error('La transferencia ya fue procesada.');

                    const reads: Promise<any>[] = [
                        tx.get(destInvRef),
                        ...destBatchRefs.map(r => tx.get(r)),
                    ];
                    let destCostSnap: any = null;
                    if (!isSameLocation && destCostRef) reads.push(tx.get(destCostRef));

                    const results = await Promise.all(reads);
                    let idx = 0;

                    const destInvSnap = results[idx++];
                    const destBatchSnapsTX = destBatchRefs.map(() => results[idx++]);
                    if (!isSameLocation && destCostRef) destCostSnap = results[idx++];

                    const destPrevAvail = destInvSnap.exists ? Number((destInvSnap.data() as any).available || 0) : 0;
                    const destNewAvail = this.to6(destPrevAvail + totalQty);

                    // Inventario destino
                    if (destInvSnap.exists) {
                        tx.update(destInvRef, {
                            available: destNewAvail,
                            last_transfer_in: this.ts()
                        });
                    } else {
                        tx.set(destInvRef, {
                            material_key: matKey,
                            name: item.name || '',
                            sku: item.sku || '',
                            area_key: toAreaKey,
                            location_key: toLocKey,
                            available: totalQty,
                            metric: item.metric || null,
                            measure: item.measure || null,
                            type: item.inventory?.type || item.type || null,
                            category_key: item.category_key || item.material?.category_key || null,
                            account: accountKey,
                            creation_date: this.ts(),
                            last_transfer_in: this.ts(),
                        });
                    }

                    // Merge batches en destino
                    const destMap = new Map<string, { ref: any, data: any }>();
                    destBatchSnapsTX.forEach(d => {
                        if (d.exists) {
                            const data = d.data() as any;
                            if (data?.batch_key) destMap.set(data.batch_key, { ref: d.ref, data });
                        }
                    });

                    for (const u of usedBatches) {
                        if (destMap.has(u.batch_key)) {
                            const { ref, data } = destMap.get(u.batch_key)!;
                            const newQty = this.to6(Number(data.quantity || 0) + u.qty);
                            const patch: any = { quantity: newQty, status: 'active' };
                            if (data?.status === 'depleted' && newQty > this.EPS) {
                                patch.depleted_date = null;
                                patch.activation_date = this.ts();
                            }
                            tx.update(ref, patch);
                        } else {
                            tx.set(destBatchesCol.doc(u.batch_key), {
                                batch_key: u.batch_key,
                                batch_code: u.batch_code,
                                entry_date: u.entry_date,
                                expiration_date: u.expiration_date || null,
                                quantity: this.to6(u.qty),
                                unit_price: Number(u.unit_price || 0),
                                status: 'active',
                                created_from: 'transfer_in',
                                source_key: transfer.$key,
                            });
                        }
                    }

                    // WAC destino (solo cross-location)
                    if (!isSameLocation && destCostRef) {
                        const destCostExists = destCostSnap?.exists || false;
                        const destPrevPool = destCostExists ? Number((destCostSnap.data() as any)?.qty_pool || 0) : 0;
                        const destPrevAvg = destCostExists ? Number((destCostSnap.data() as any)?.avg_price || 0) : 0;

                        const transferUnit = totalQty > this.EPS ? this.to6(totalCost / totalQty) : 0;

                        const wacIn = this.applyWacCoreWriteOnly(
                            tx, destCostRef, matKey, toLocKey,
                            destCostExists, destPrevPool, destPrevAvg,
                            totalQty, transferUnit, 'transfer', transfer.$key
                        );

                        this.logInventoryMovementBatch(tx, {
                            type: 'transfer_in',
                            source_key: transfer.$key,
                            material: { material_key: matKey, name: item.name },
                            quantity_before: destPrevAvail,
                            quantity_after: destNewAvail,
                            quantity_changed: totalQty,
                            measure: item.metric?.symbol || null,
                            location_key: toLocKey,
                            area_key: toAreaKey,
                            user,
                            avg_price_location: wacIn.newAvg,
                            detail: { received_batches: usedBatches }
                        });
                    } else {
                        // Same location: log sin WAC
                        this.logInventoryMovementBatch(tx, {
                            type: 'transfer_in',
                            source_key: transfer.$key,
                            material: { material_key: matKey, name: item.name },
                            quantity_before: destPrevAvail,
                            quantity_after: destNewAvail,
                            quantity_changed: totalQty,
                            measure: item.metric?.symbol || null,
                            location_key: toLocKey,
                            area_key: toAreaKey,
                            user,
                            avg_price_location: null,
                            detail: { received_batches: usedBatches }
                        });
                    }

                    // (Opcional) marcar material como recibido dentro del transfer
                    // tx.update(transferRef, { updated_at: this.ts() });
                });
            }

            // 2) Marcar transfer recibida
            await transferRef.update({
                status: 'received',
                received_details: {
                    received_by: user,
                    signature_url: signatureUrl,
                    received_at: this.ts(),
                },
                updated_at: this.ts(),
            });

            // 3) Si venía de request → marcar request completed
            if (transfer.request_id) {
                await firestore.collection(`accounts_abc/${accountKey}/transfer_requests`)
                    .doc(transfer.request_id)
                    .update({
                        status: 'received',
                        received_at: this.ts(),
                        received_by: user,
                        updated_at: this.ts()
                    });
            }

            // 4) notificar al remitente que su transferencia fue recibida
            try {
                await this.notificationService.sendNotification({
                    type: 'transfer_accepted',
                    status: 'unread',
                    title: 'Transferencia recibida',
                    message: `${transfer.to_location.name} ha aceptado y recibido tu transferencia`,
                    target_location_key: transfer.from_location.$key,
                    target_area_key: transfer.from_area.$key,
                    source_location_key: transfer.to_location.$key,
                    source_area_key: transfer.to_area.$key,
                    reference_key: transfer.$key,
                    reference_type: 'transfer'
                });
            } catch (e) {
                console.error('Error al enviar notificación:', e);
            }

            return true;
        } catch (e) {
            console.error('Error en acceptTransfer:', e);
            throw e;
        }
    }

    async rejectTransfer(transfer: any, signature: any, reason: string) {
        try {
            const accountKey = this.auth.account_key;
            const firestore = this.firestore.firestore;
            const user = this.auth.user_resume;

            if (!transfer?.$key) throw new Error('Transfer inválida.');
            if (transfer.status !== 'in_transit') throw new Error('Solo se puede rechazar una transferencia en tránsito.');
            if (!reason?.trim()) throw new Error('Debes indicar un motivo.');

            // Firma
            const folder = `signatures/${accountKey}`;
            const signatureUrl = await this.cloudinary.uploadImage(signature, folder);

            const transferRef = firestore
                .collection(`accounts_abc/${accountKey}/transfers`)
                .doc(transfer.$key);

            const fromLocKey = transfer.from_location.$key;
            const toLocKey = transfer.to_location.$key;
            const isSameLocation = fromLocKey === toLocKey;

            const fromAreaKey = transfer.from_area.$key;

            // Por cada material: devolver al ORIGEN usando used_batches
            for (const item of (transfer.materials || [])) {
                const matKey = item.material_key;
                if (!matKey) continue;

                const usedRaw = Array.isArray(item.used_batches) ? item.used_batches : [];
                if (!usedRaw.length) {
                    throw new Error(`No hay used_batches para ${item.name || matKey}.`);
                }

                const usedBatches = usedRaw
                    .map((b: any) => ({
                        batch_key: b.batch_key,
                        batch_code: b.batch_code,
                        qty: Number(b.used_from_batch ?? b.transferred_quantity ?? b.quantity ?? 0),
                        unit_price: Number(b.unit_price || 0),
                        entry_date: b.entry_date,
                        expiration_date: b.expiration_date || null,
                    }))
                    .filter((u: any) => u.qty > this.EPS);

                const totalQty = this.to6(usedBatches.reduce((s: number, x: any) => s + x.qty, 0));
                if (totalQty <= this.EPS) continue;

                // (opcional, por logging)
                const totalCost = Number(
                    usedBatches.reduce((s: number, x: any) => s + x.qty * x.unit_price, 0).toFixed(6)
                );

                const originInvRef = firestore.doc(
                    `accounts_abc/${accountKey}/inventory/${matKey}_${fromLocKey}_${fromAreaKey}`
                );
                const originBatchesCol = originInvRef.collection('batches');

                // prelectura batches origen (refs)
                const originBatchesSnap = await originBatchesCol.get();
                const originBatchRefs = originBatchesSnap.docs.map(d => d.ref);

                await firestore.runTransaction(async (tx) => {
                    // idempotencia
                    const tSnap = await tx.get(transferRef);
                    if (!tSnap.exists) throw new Error('Transfer no existe.');
                    const tData: any = tSnap.data();
                    if (tData.status !== 'in_transit') throw new Error('La transferencia ya fue procesada.');

                    // lecturas dentro de tx
                    const reads: Promise<any>[] = [
                        tx.get(originInvRef),
                        ...originBatchRefs.map(r => tx.get(r)),
                    ];

                    // ✅ si cross-location: también leemos costRef para revertir pool
                    let costRef: any = null;
                    if (!isSameLocation) {
                        costRef = firestore.doc(this.costDocPath(matKey, fromLocKey));
                        reads.push(tx.get(costRef));
                    }

                    const results = await Promise.all(reads);
                    let idx = 0;

                    const originInvSnap = results[idx++];
                    const originBatchSnapsTX = originBatchRefs.map(() => results[idx++]);

                    let costSnap: any = null;
                    if (!isSameLocation && costRef) costSnap = results[idx++];

                    if (!originInvSnap.exists) {
                        throw new Error(`Inventario origen no existe para ${item.name || matKey}.`);
                    }

                    const originPrevAvail = Number((originInvSnap.data() as any).available || 0);
                    const originNewAvail = this.to6(originPrevAvail + totalQty);

                    // devolver available al origen
                    tx.update(originInvRef, { available: originNewAvail });

                    // merge batches de vuelta al origen
                    const originMap = new Map<string, { ref: any, data: any }>();
                    originBatchSnapsTX.forEach(d => {
                        if (d.exists) {
                            const data = d.data() as any;
                            if (data?.batch_key) originMap.set(data.batch_key, { ref: d.ref, data });
                        }
                    });

                    for (const u of usedBatches) {
                        if (originMap.has(u.batch_key)) {
                            const { ref, data } = originMap.get(u.batch_key)!;
                            const newQty = this.to6(Number(data.quantity || 0) + u.qty);
                            const patch: any = { quantity: newQty, status: 'active' };
                            if (data?.status === 'depleted' && newQty > this.EPS) {
                                patch.depleted_date = null;
                                patch.activation_date = this.ts();
                            }
                            tx.update(ref, patch);
                        } else {
                            tx.set(originBatchesCol.doc(u.batch_key), {
                                batch_key: u.batch_key,
                                batch_code: u.batch_code,
                                entry_date: u.entry_date,
                                expiration_date: u.expiration_date || null,
                                quantity: this.to6(u.qty),
                                unit_price: Number(u.unit_price || 0),
                                status: 'active',
                                created_from: 'transfer_rejected_return',
                                source_key: transfer.$key,
                            });
                        }
                    }

                    // ✅ revertir qty_pool SOLO si cross-location (volver a sumar lo que salió)
                    if (!isSameLocation && costRef) {
                        const exists = !!costSnap?.exists;
                        const prevPool = exists ? Number((costSnap.data() as any)?.qty_pool || 0) : 0;
                        const prevAvg = exists ? Number((costSnap.data() as any)?.avg_price || 0) : 0;

                        // Para que NO cambie el avg en el “reverso”, metemos unitIn = prevAvg
                        const unitIn = prevAvg;

                        this.applyWacCoreWriteOnly(
                            tx,
                            costRef,
                            matKey,
                            fromLocKey,
                            exists,
                            prevPool,
                            prevAvg,
                            totalQty,
                            unitIn,
                            'transfer_reject',
                            transfer.$key
                        );
                    }

                    // log devolución
                    this.logInventoryMovementBatch(tx, {
                        type: 'transfer_return',
                        source_key: transfer.$key,
                        material: { material_key: matKey, name: item.name },
                        quantity_before: originPrevAvail,
                        quantity_after: originNewAvail,
                        quantity_changed: totalQty,
                        measure: item.metric?.symbol || null,
                        location_key: fromLocKey,
                        area_key: fromAreaKey,
                        user,
                        detail: {
                            returned_batches: usedBatches,
                            reason,
                            total_cost: totalCost,
                        }
                    });
                });
            }

            // Marcar transfer rechazada
            await transferRef.update({
                status: 'rejected',
                rejection_details: {
                    rejected_by: user,
                    signature_url: signatureUrl,
                    rejected_at: this.ts(),
                    reason,
                },
                updated_at: this.ts(),
            });

            // Si venía de request → podés dejar request como fulfilled con detalles de rechazo
            if (transfer.request_id) {
                await firestore.collection(`accounts_abc/${accountKey}/transfer_requests`)
                    .doc(transfer.request_id)
                    .update({
                        status: 'rejected',
                        rejection_transfer_details: {
                            rejected_by: user,
                            signature_url: signatureUrl,
                            rejected_at: this.ts(),
                            rejected_reason: reason,
                        },
                        updated_at: this.ts(),
                    });
            }

            // notificar al remitente que la transferencia fue rechazada
            try {
                await this.notificationService.sendNotification({
                    type: 'transfer_rejected',
                    status: 'unread',
                    title: 'Transferencia rechazada',
                    message: `${transfer.to_location.name} ha rechazado tu transferencia. Motivo: ${reason}`,
                    target_location_key: transfer.from_location.$key,
                    target_area_key: transfer.from_area.$key,
                    source_location_key: transfer.to_location.$key,
                    source_area_key: transfer.to_area.$key,
                    reference_key: transfer.$key,
                    reference_type: 'transfer'
                });
            } catch (e) {
                console.error('Error al enviar notificación:', e);
            }

            return true;
        } catch (e) {
            console.error('Error en rejectTransfer:', e);
            throw e;
        }
    }

    async createMaterialsArray(materials: any[], type: 'commodity' | 'disposable' | 'cleaning') {
        const accountKey = this.auth.account_key;

        try {
            const categoriesSnapshot: any = await this.firestore
                .collection(`accounts_abc/${accountKey}/categories_materials`)
                .get()
                .toPromise();

            const categoriesMap = new Map(
                categoriesSnapshot.docs.map((doc: any) => [
                    doc.data().name.toLowerCase(),
                    { $key: doc.id, ...doc.data() },
                ])
            );

            const batches: WriteBatch[] = [];
            let currentBatch: any = this.firestore.firestore.batch();
            let batchCounter = 0;

            let importedCount = 0;
            let failedCount = 0;

            const snapshot = await this.getRef(`accounts_abc/${this.auth.account_key}/materials`).ref
                .where('type', '==', type)
                .orderBy('sku', 'desc')
                .limit(1)
                .get() as any;

            const last_item = snapshot.docs[0]?.data();
            const last_sku = getSkuNumber(last_item?.sku);

            for (const material of materials) {
                try {
                    if (!material?.category || !material?.measure) {
                        throw new Error('Material inválido.');
                    }

                    material.sku = generateSku(type, last_sku + (importedCount + 1));

                    const categoryName = material.category.trim().toLowerCase();

                    let categoryData = categoriesMap.get(categoryName);

                    if (!categoryData) {
                        const categoryRef = this.firestore
                            .collection(`accounts_abc/${accountKey}/categories_materials`)
                            .doc().ref;

                        categoryData = {
                            $key: categoryRef.id,
                            name: material.category,
                            active: true,
                            color: '#000000',
                            creation_date: new Date(),
                        };

                        categoriesMap.set(categoryName, categoryData);
                        currentBatch.set(categoryRef, categoryData);
                        batchCounter++;

                    }

                    const materialRef = this.firestore
                        .collection(`accounts_abc/${accountKey}/materials`)
                        .doc().ref;

                    material.measure = material.measure.toLowerCase();
                    const normalizedMaterial = this.normalizeImportedMaterial(material, categoryData, type);

                    currentBatch.set(materialRef, normalizedMaterial);

                    if (!material.sku || material.sku.trim() === '') {
                        material.sku = generateSku(type, snapshot.size + importedCount + 1);
                    }

                    batchCounter++;
                    importedCount++;
                } catch {
                    failedCount++;
                }

                if (batchCounter >= 500) {
                    batches.push(currentBatch);
                    currentBatch = this.firestore.firestore.batch();
                    batchCounter = 0;
                }
            }

            if (batchCounter > 0) {
                batches.push(currentBatch);
            }

            await Promise.all(batches.map(batch => batch.commit()));

            const total = importedCount + failedCount;
            const status: 'success' | 'partial' | 'error' =
                importedCount === total ? 'success' :
                    importedCount === 0 ? 'error' : 'partial';

            return { status };
        } catch (error) {
            console.error('Error en la importación:', error);
            return { status: 'error' };
        }
    }

    private normalizeImportedMaterial(material: any, categoryData: any, type: 'commodity' | 'disposable' | 'cleaning') {
        return {
            active: true,
            category: {
                $key: categoryData.$key,
                active: categoryData.active,
                color: categoryData.color,
                name: categoryData.name,
                creation_date: new Date()
            },
            category_key: categoryData.$key,
            creation_date: new Date(),
            measure: this.abc.getMeasureType(material.measure),
            metric: {
                name: this.abc.getMetricName(material.measure),
                symbol: this.abc.getMetricSymbol(material.measure)
            },
            name: material.name || '',
            sku: material.sku || '',
            expiration_type: material.expiration_type || '',
            type: type,
            updated_date: new Date()
        };
    }

    async createSuppliersArray(suppliers: any[]) {
        const accountKey = this.auth.account_key;
        const batches: WriteBatch[] = [];
        let currentBatch: any = this.firestore.firestore.batch();
        let batchCounter = 0;

        for (const supplier of suppliers) {
            // Validar campos obligatorios
            if (!supplier.name || !supplier.phone || !supplier.email || !supplier.person_name) {
                console.warn(`Proveedor omitido por datos incompletos:`, supplier);
                continue;
            }

            const supplierRef = this.firestore.collection(`accounts_abc/${accountKey}/main/suppliers`).doc().ref;
            const normalizedSupplier = this.normalizeSupplier(supplier);
            currentBatch.set(supplierRef, normalizedSupplier);
            batchCounter++;

            if (batchCounter >= 500) {
                batches.push(currentBatch);
                currentBatch = this.firestore.firestore.batch();
                batchCounter = 0;
            }
        }

        if (batchCounter > 0) {
            batches.push(currentBatch);
        }

        // Ejecutar los batch commits en paralelo
        return Promise.all(batches.map(batch => batch.commit()));
    }

    private normalizeSupplier(supplier: any) {
        return {
            account_key: this.auth.account_key,
            address: supplier.address ? supplier.address.trim() : '', // Opcional
            name: supplier.name,
            email: supplier.email,
            phone: supplier.phone,
            contacts: [
                {
                    name: supplier.person_name.trim(),
                    phone: supplier.phone,
                    email: supplier.email ? supplier.email.trim() : ''
                }
            ],
        };
    }

    async calculateAvailability(recipe: any, locationKey: any = this.auth.location_key, areaKey: any = this.auth.area_key) {

        const materialsMap: { [$key: string]: { quantity: number; measure: any; measureType: string; name?: string, sku: string, source: string } } = {};

        // Base recipe materials
        for (const ing of recipe.recipe || []) {
            const id = ing.material.$key;
            if (!materialsMap[id]) {
                materialsMap[id] = {
                    quantity: ing.quantity,
                    measure: ing.metric,
                    measureType: ing.material.measure,
                    name: ing.material.name,
                    sku: ing.material.sku,
                    source: 'recipe'
                };
            } else {
                materialsMap[id].quantity += ing.quantity;
            }
        }

        // Modifier materials (only those with selected: true)
        for (const mod of recipe.modifiers || []) {
            for (const opt of mod.options || []) {
                const id = opt.material.$key;
                if (!materialsMap[id]) {
                    materialsMap[id] = {
                        quantity: +opt.quantity,
                        measure: opt.metric,
                        measureType: opt.material.measure,
                        name: opt.material.name,
                        sku: opt.material.sku,
                        source: 'modifier'
                    };
                } else {
                    materialsMap[id].quantity += Number(opt.quantity);
                }
            }
        }

        const salesDisposables = recipe.sales_disposables || [];
        const productionDisposables = recipe.production_disposables || [];

        const allDisposables = [...salesDisposables, ...productionDisposables];


        for (const ing of allDisposables) {
            const material = ing.material || ing;
            const id = material.$key;

            if (!materialsMap[id]) {
                materialsMap[id] = {
                    quantity: ing.quantity || 0,
                    measure: material.measure || ing.metric?.symbol,
                    measureType: material.metric || material.measure,
                    name: material.name,
                    sku: material.sku,
                    source: 'disposable'
                };
            } else {
                materialsMap[id].quantity += ing.quantity || 0;
            }
        }

        let minAvailable = Infinity;
        let limitingMaterial = null;
        let hasMissing = false;
        let missingMaterials: any[] = [];
        let recipeItems = [];
        let disposableMaterials: any[] = [];
        let modifierMaterials: any[] = [];


        for (const [id, usage] of Object.entries(materialsMap)) {
            const documentId = `${id}_${locationKey}_${areaKey}`;

            const inv: any = await this.getDocument(`accounts_abc/${this.auth.account_key}/inventory`, documentId).catch(() => null);

            if (!inv) {
                hasMissing = true;
                missingMaterials.push({
                    id,
                    name: usage.name || 'Material no disponible',
                    available: 0,
                    used_per_unit: usage.quantity,
                    sku: usage.sku,
                    metric: usage.measure,
                    type: usage.source

                });
                continue;
            }

            const stockQty = inv.available || 0;
            const stockMeasure = inv.metric?.symbol || usage.measure.symbol;
            const stockMeasureType = inv.measure || usage.measureType;
            const convertedQty = this.abc.convertMeasure(
                stockMeasureType,
                stockQty,
                stockMeasure,
                usage.measure.symbol
            );

            const availableUnits = Math.floor(convertedQty / usage.quantity);
            const item = {
                ...usage,
                availableUnits,
                available: {
                    quantity: convertedQty,
                    metric: usage.measure,
                },
                type: usage.source

            };

            if (item.source === "disposable") {
                disposableMaterials.push(item);
            } else if (item.source === "modifier") {
                modifierMaterials.push(item);
            } else {
                recipeItems.push(item);
            }
            if (availableUnits < minAvailable) {
                minAvailable = availableUnits;
                limitingMaterial = {
                    id,
                    name: inv.name || id,
                    available: convertedQty,
                    used_per_unit: usage.quantity,
                    sku: usage.sku,
                    metric: usage.measure,
                    type: usage.source
                };
            }

        }

        return {
            recipe_items: recipeItems,
            max_available: minAvailable === Infinity || hasMissing ? 0 : minAvailable,
            limiting_material: missingMaterials.length > 0 ? missingMaterials[0] : limitingMaterial,
            missing_materials: missingMaterials,
            disposable_materials: disposableMaterials,
            modifier_materials: modifierMaterials,
            out_of_stock: minAvailable === Infinity || hasMissing
        };
    }

    async processSales(data: any, locationKey: any = this.auth.location_key, areaKey: any = this.auth.area_key) {

        const accountKey = this.auth.account_key;
        // const locationKey = this.auth.location_key;
        // const areaKey = this.auth.area_key;

        const results = {
            processed: 0,
            errors: [] as { sku: string; reason: string }[]
        };

        for (const entry of data) {
            const { sku, quantity } = entry;
            const recipesSnap = await this.getRef(`accounts_abc/${accountKey}/menu_recipes`).ref
                .where('sku', '==', sku)
                .limit(1)
                .get()
                .catch(() => null);

            if (!recipesSnap || recipesSnap.empty) {
                results.errors.push({ sku, reason: 'Receta no encontrada' });
                continue;
            }

            const recipeDoc = recipesSnap.docs[0];
            const recipeData: any = recipeDoc.data();
            const recipeKey = recipeDoc.id;

            const materialsMap: { [id: string]: { quantity: number; measure: string; measureType: string; name?: string } } = {};

            for (const ing of recipeData.recipe || []) {
                const $key = ing.material.$key;
                if (!materialsMap[$key]) {
                    materialsMap[$key] = {
                        quantity: ing.quantity * quantity,
                        measure: ing.metric.symbol,
                        measureType: ing.material.measure,
                        name: ing.material.name
                    };
                } else {
                    materialsMap[$key].quantity += ing.quantity * quantity;
                }
            }

            let allSuccess = true;
            const logMaterials: any[] = [];

            // 🔥 Empezamos batch write
            const batch = this.firestore.firestore.batch();

            for (const [$key, usage] of Object.entries(materialsMap)) {
                const path = `accounts_abc/${accountKey}/inventory/${$key}_${locationKey}_${areaKey}`;
                const invDocRef = this.firestore.doc(path).ref;

                const inv: any = await this.getDocument(`accounts/${accountKey}/abc`, `materials/inventory/${$key}_${locationKey}_${areaKey}`).catch(() => null);

                if (!inv || inv.available == null) {
                    results.errors.push({ sku, reason: `Material faltante en inventario: ${usage.name || $key}` });
                    allSuccess = false;
                    break;
                }

                const batchesSnap = await this.getRef(`accounts_abc/${accountKey}/inventory/${$key}_${locationKey}_${areaKey}/batches`).ref
                    .where('status', '==', 'active')
                    .orderBy('entry_date', 'asc')
                    .get()
                    .catch(() => null);

                if (!batchesSnap || batchesSnap.empty) {
                    results.errors.push({ sku, reason: `No hay lotes disponibles para: ${usage.name || $key}` });
                    allSuccess = false;
                    break;
                }

                let remainingQty = this.abc.convertMeasure(
                    usage.measureType,
                    usage.quantity,
                    usage.measure,
                    inv.metric?.symbol || usage.measure
                );

                const batchLogs: any[] = [];

                for (const batchDoc of batchesSnap.docs) {
                    const batchData: any = batchDoc.data();
                    const batchId = batchDoc.id;

                    const batchQty = batchData.quantity || 0;
                    const batchUnit = batchData.unit || usage.measure;

                    const toDeduct = Math.min(batchQty, remainingQty);

                    if (toDeduct > 0) {
                        const newBatchQty = batchQty - toDeduct;

                        const batchDocRef = this.firestore.doc(`accounts_abc/${accountKey}/inventory/${$key}_${locationKey}_${areaKey}/batches/${batchId}`).ref;

                        const updateData: any = {
                            quantity: newBatchQty,
                            status: newBatchQty <= 0 ? 'depleted' : 'active'
                        };

                        if (newBatchQty <= 0) {
                            updateData.depleted_date = this.ts();
                        }

                        batch.update(batchDocRef, updateData);

                        console.log({
                            batch_id: batchId,
                            original_quantity: Number(batchQty.toFixed(2)),
                            quantity_deducted: Number(toDeduct.toFixed(2)),
                            new_quantity: Number(newBatchQty.toFixed(2)),
                            unit: batchUnit
                        });

                        batchLogs.push({
                            batch_id: batchId,
                            original_quantity: Number(batchQty.toFixed(2)),
                            quantity_deducted: Number(toDeduct.toFixed(2)),
                            new_quantity: Number(newBatchQty.toFixed(2)),
                            unit: batchUnit
                        });

                        remainingQty -= toDeduct;
                    }

                    if (remainingQty <= 0) break;
                }

                if (remainingQty > 0) {
                    results.errors.push({ sku, reason: `Inventario insuficiente (por lotes) para: ${usage.name || $key}` });
                    allSuccess = false;
                    break;
                }

                const newAvailableQty = (inv.available || 0) - this.abc.convertMeasure(
                    usage.measureType,
                    usage.quantity,
                    usage.measure,
                    inv.metric?.symbol || usage.measure
                );

                console.log(Number(newAvailableQty));

                batch.update(invDocRef, {
                    available: Number(newAvailableQty)
                });

                logMaterials.push({
                    material_id: $key,
                    name: usage.name,
                    quantity: Number(usage.quantity.toFixed(2)),
                    unit: usage.measure,
                    date: new Date(),
                    user: this.auth.user_resume,
                    location_key: locationKey,
                    area_key: areaKey,
                    batches: batchLogs
                });
            }

            if (allSuccess) {
                const logDocRef = this.firestore.collection(`accounts_abc/${accountKey}/reports/inventory_logs`).doc().ref;
                batch.set(logDocRef, {
                    date: new Date(),
                    source: 'Excel Upload',
                    type: 'sale',
                    sku,
                    recipe_key: recipeKey,
                    quantity,
                    location_key: locationKey,
                    area_key: areaKey,
                    user: this.auth.user_resume,
                    materials: logMaterials
                });

                // 🔥 Ejecutar el batch
                await batch.commit();

                results.processed++;
            }
        }

        return results;
    }

    async processWithdrawals(items: any[], withdrawalForm: any) {
        try {
            const accountKey = this.auth.account_key;
            const firestore = this.firestore.firestore;

            const locationKey = withdrawalForm.location_key;
            const areaKey = withdrawalForm.area_key;
            const user = this.auth.user_resume;

            const withdrawalKey = this.firestore.createId();
            const dateCode = new Date().toISOString().slice(2, 10).replace(/-/g, '');
            const randomCode = withdrawalKey.slice(-4).toUpperCase();
            const folio = `SAL-${dateCode}-${randomCode}`;

            const withdrawalItems: any[] = [];
            const errors: any[] = [];

            for (const item of items) {
                const materialKey = item.material.material_key || item.material.$key;
                const materialName = item.material.name;
                const measure = item.conversion_factor?.symbol ?? item.material.metric.symbol;
                const measureType = item.material.measure;
                const withdrawalQty = Number(item.withdrawal_quantity || 0);

                const invId = `${materialKey}_${locationKey}_${areaKey}`;
                const invRef = firestore.doc(`accounts_abc/${accountKey}/inventory/${invId}`);
                const batchesCol = invRef.collection('batches');
                const costRef = firestore.doc(`accounts_abc/${accountKey}/inventory_cost/${materialKey}_${locationKey}`);

                // Pre-lectura: si UI no trae lotes, usamos FIFO
                let selected = item.batches as Array<{ batch_key: string; selected_quantity: number; batch_code?: string }> || [];
                if (!selected.length) {
                    const pre = await batchesCol.where('status', '==', 'active').orderBy('entry_date', 'asc').get();
                    selected = pre.docs.map(d => ({ batch_key: d.id, selected_quantity: Number((d.data() as any).quantity || 0), batch_code: (d.data() as any).batch_code }));
                }

                await firestore.runTransaction(async (tx) => {
                    const invSnap = await tx.get(invRef);
                    if (!invSnap.exists) {
                        errors.push({ material: materialName, reason: 'Inventario no encontrado' });
                        return;
                    }
                    const inv: any = invSnap.data();
                    const invUnit = inv?.metric?.symbol || item.material.metric.symbol;

                    // Convertir retiro a unidad base
                    const convertedQty = Number(this.abc.convertMeasure(
                        measureType,
                        withdrawalQty,
                        measure,
                        invUnit
                    ));

                    // Leer lotes seleccionados
                    const usedBatches: any[] = [];
                    let sumConverted = 0;

                    for (const sel of selected) {
                        const bRef = batchesCol.doc(sel.batch_key);
                        const bSnap = await tx.get(bRef);
                        if (!bSnap.exists) {
                            errors.push({ material: materialName, reason: `Lote ${sel.batch_key} no encontrado` });
                            return;
                        }
                        const b = bSnap.data() as any;
                        const take = Number(this.to6(this.abc.convertMeasure(
                            measureType,
                            Number(sel.selected_quantity || 0),
                            measure,
                            invUnit
                        )));
                        if (Number(b.quantity || 0) + this.EPS < take) {
                            errors.push({ material: materialName, reason: `Lote ${b.batch_code || sel.batch_key} con cantidad insuficiente` });
                            return;
                        }
                        sumConverted = this.to6(sumConverted + take);
                        usedBatches.push({
                            batch_key: bSnap.id,
                            batch_code: b.batch_code,
                            quantity_deducted_original: sel.selected_quantity,
                            quantity_deducted_converted: take,
                            quantity_before: Number(b.quantity || 0),
                            quantity_after: this.to6(Number(b.quantity || 0) - take),
                            unit_price: Number(b.unit_price || 0)
                        });
                    }

                    // Validar suma contra retirado convertido
                    if (Math.abs(sumConverted - convertedQty) > 1e-4) {
                        errors.push({ material: materialName, reason: `Diferencia entre cantidad retirada y total de lotes (sum=${sumConverted}, req=${convertedQty})` });
                        return;
                    }

                    // WAC Exit (ubicación)
                    const wac = await this.applyWacExit(tx, costRef, convertedQty, 'withdrawal', withdrawalKey);

                    // Actualizar lotes
                    for (const ub of usedBatches) {
                        const bRef = batchesCol.doc(ub.batch_key);
                        const patch: any = { quantity: ub.quantity_after, status: ub.quantity_after <= this.EPS ? 'depleted' : 'active' };
                        if (ub.quantity_after <= this.EPS) patch.depleted_date = this.ts();
                        tx.update(bRef, patch);
                    }

                    // Actualizar inventario área
                    const beforeAvail = Number(inv.available || 0);
                    const afterAvail = this.to6(beforeAvail - convertedQty);
                    tx.update(invRef, { available: afterAvail });

                    // Armar item de encabezado
                    withdrawalItems.push({
                        material_key: materialKey,
                        material_name: materialName,
                        withdrawal_quantity: withdrawalQty,
                        converted_quantity: convertedQty,
                        withdrawal_metric: item.material.metric,
                        withdrawal_measure: item.material.measure,
                        type: item.material.type,
                        category: item.material.category || null
                    });

                    // Log
                    this.logInventoryMovementBatch(tx, {
                        type: 'withdrawal',
                        source_key: withdrawalKey,
                        material: item.material,
                        quantity_before: beforeAvail,
                        quantity_after: afterAvail,
                        quantity_changed: -convertedQty,
                        measure: invUnit,
                        location_key: locationKey,
                        area_key: areaKey,
                        user,
                        detail: {
                            folio,
                            used_batches: usedBatches,
                            withdrawal_quantity: withdrawalQty,
                            converted_quantity: convertedQty,
                            avg_price_location_before: wac.prevAvg,
                            avg_price_location_after: wac.newAvg,
                            qty_pool_before: wac.prevPool,
                            qty_pool_after: wac.newPool
                        }
                    });
                });
            }

            // Guardar encabezado si hubo al menos un Descripción procesado
            if (withdrawalItems.length > 0) {
                await this.firestore.collection(`accounts_abc/${accountKey}/withdrawals`)
                    .doc(withdrawalKey)
                    .set({
                        withdrawal_key: withdrawalKey,
                        folio,
                        location_key: locationKey,
                        area_key: areaKey,
                        reason: withdrawalForm.reason,
                        created_at: new Date(),
                        created_by: user,
                        items: withdrawalItems
                    });

                return {
                    processed: withdrawalItems.length,
                    errors,
                    folio
                };
            }

            return { processed: 0, errors, folio: null };

        } catch (err) {
            console.error('Error en processWithdrawals:', err);
            throw err;
        }
    }

    logInventoryMovementBatch(batch: any, movement: any) {
        const docRef = this.firestore
            .collection(`accounts_abc/${this.auth.account_key}/inventory_movements`)
            .doc().ref;

        const isTransfer = movement.type === 'transfer';
        const mainItem = movement.material || movement.equipment;

        if (mainItem?.movement_type === 'non_inventory_usage') {
            return;
        }

        const data: any = {
            date: new Date(),
            type: movement.type,
            source_key: movement.source_key,
            ...(isTransfer
                ? {
                    inventory: mainItem,
                    inventory_key: mainItem?.$key || mainItem?.material_key || mainItem?.equipment_key,
                }
                : {
                    material: mainItem,
                    material_key: mainItem?.$key || mainItem?.material_key || mainItem?.equipment_key,
                }),
            material_name: mainItem?.name,
            sku: mainItem?.sku || '',
            quantity_before: movement.quantity_before,
            quantity_after: movement.quantity_after,
            quantity_changed: movement.quantity_changed,
            measure: movement.measure,
            location_key: movement.location_key,
            area_key: movement.area_key,
            user: movement.user,
            detail: movement.detail || null
        }
        if (movement.type === 'inventory_count' && movement.counting_key) {
            data.counting_key = movement.counting_key;
        }
        batch.set(docRef, data);
    }

    logTransferMovementBatch(batch: any, movement: any) {
        const docRef = this.firestore
            .collection(`accounts_abc/${this.auth.account_key}/transfer_movements`)
            .doc().ref;

        const isTransfer = movement.type === 'transfer';
        // Usar material o equipment según disponibilidad
        const mainItem = movement.material || movement.equipment;

        batch.set(docRef, {
            date: new Date(),
            type: movement.type,
            source_key: movement.source_key,
            ...(isTransfer
                ? {
                    inventory: mainItem,
                    inventory_key: mainItem?.$key || mainItem?.material_key || mainItem?.equipment_key,
                }
                : {
                    material: mainItem,
                    material_key: mainItem?.$key || mainItem?.material_key || mainItem?.equipment_key,
                }),
            material_name: mainItem?.name,
            sku: mainItem?.sku || '',
            quantity_before: movement.quantity_before,
            quantity_after: movement.quantity_after,
            quantity_changed: movement.quantity_changed,
            measure: movement.measure,
            location_key: movement.location_key,
            area_key: movement.area_key,
            user: movement.user,
            detail: movement.detail || null
        });
    }

    async validateUniqueFields({
        sku,
        category,
        path,
    }: {
        sku?: string | null;
        category?: string | null;
        path?: string | null;
    }): Promise<{ sku?: string; category?: string; code?: string; error?: string } | null> {
        try {
            const values = { sku, category };
            if (!Object.values(values).some(v => !!v)) {
                return { error: 'Se debe enviar al menos un campo para validar' };
            }

            const errors: { sku?: string; category?: string; code?: string } = {};
            const basePath = `accounts_abc/${this.auth.account_key}`;

            const promises: Promise<void>[] = [];

            const collectionsSkuName = [
                `${basePath}/materials`,
                `${basePath}/equipment`,
                `${basePath}/menu_recipes`
            ];

            if (sku) {
                collectionsSkuName.forEach(col => {
                    const obs = this.firestore
                        .collection(col, ref => ref.where('sku', '==', sku).limit(1))
                        .get();
                    promises.push(
                        lastValueFrom(obs).then(snap => {
                            if (!snap.empty) errors.sku ??= `El SKU ya existe.`;
                        })
                    );
                });
            }

            if (category && path) {
                const obs = this.firestore
                    .collection(path, ref => ref.where('name', '==', category).limit(1))
                    .get();

                const snap = await lastValueFrom(obs);
                if (!snap.empty) {
                    errors.category ??= 'La categoria ya existe.';
                }
            }

            await Promise.all(promises);

            return Object.keys(errors).length ? errors : null;
        } catch (error) {
            console.error(error);
            throw new Error(`Error validando campos únicos: ${error}`);
        }
    }

    async getInventoryItems(results: any[], location: string, area: string) {
        const items = await Promise.all(
            results.map(async (item) => {
                const id = `${item.$key}_${location}_${area}`;
                const docSnap: any = await this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/inventory`).doc(id).get();
                const data = docSnap.data();
                return { ...item, $key: id, material_key: item.$key, available: data?.available ?? 0 }
            })

        );
        return items
    }

    async getActiveInventoryById(id: string) {
        const inventoryRef = this.firestore.firestore.doc(
            `accounts_abc/${this.auth.account_key}/inventory/${id}`
        );

        const inventorySnapshot = await inventoryRef.get();
        if (!inventorySnapshot.exists) {
            throw new Error(`No se encontró un inventario en Firestore con id: ${id}`);
        }

        const batchesRef = inventoryRef
            .collection("batches")
            .where("status", "==", "active")
            .orderBy("entry_date", "asc");

        const batchesSnapshots = await batchesRef.get();
        const inventory: any = inventorySnapshot.data()!;

        const batches = batchesSnapshots.docs.map((doc) => {
            const data: any = doc.data();
            return {
                $key: doc.id,
                ...data,
                entry_date: data.entry_date.toDate(),
                quantity: parseFloat(Number(data.quantity).toFixed(2)),
                metric: inventory.metric,
                measure: inventory.measure
            };
        });

        const total_available = batches.reduce(
            (acc, batch: any) => acc + (batch.quantity || 0),
            0
        );

        return {
            batches,
            total_available: parseFloat(total_available.toFixed(2))
        };

    }

    async checkForInitialInv(): Promise<boolean> {
        try {
            const accountKey = this.auth.account_key;
            const areaKey = this.auth.area_key;
            const locationKey = this.auth.location_key;

            if (!accountKey || !areaKey) return false;

            // 1) Revisar flag en el doc del área
            const areaRef = this.firestore
                .collection('accounts_abc')
                .doc(accountKey)
                .collection('areas')
                .doc(areaKey);

            const areaSnap = await areaRef.get().toPromise();

            if (areaSnap?.exists) {
                const areaData: any = areaSnap.data();
                const status = areaData?.inventory_initialization?.status;

                if (status === 'completed' || status === 'skipped') {
                    return true; // ✅ ya no mostrar
                }
            }

            // 2) Fallback: si ya hay inventario para esta área, tampoco mostrar
            const invSnap = await this.firestore
                .collection(`accounts_abc/${accountKey}/inventory`, (ref) => {
                    let q: any = ref.where('area_key', '==', areaKey);
                    q = q.where('location_key', '==', locationKey);
                    return q.limit(1);
                })
                .get()
                .toPromise();

            if (invSnap && !invSnap.empty) {
                // ✅ Ya existe inventario para el área => no mostrar wizard
                // (Opcional) Podés “autocurar” el flag para que ya quede guardado:
                // await areaRef.set({ inventory_initialization: { status: 'completed', completed_at: this.ts() } }, { merge: true });

                return true;
            }

            // 3) No hay flag y no hay inventario => mostrar wizard
            return false;

        } catch (err) {
            console.error('[checkForInitialInv]', err);
            return false;
        }
    }

    async createInitialInventory(materials: Array<any>) {
        try {
            const accountKey = this.auth.account_key;
            const user = this.auth.user_resume;
            const firestore = this.firestore.firestore;
            const area_key = this.auth.area_key;
            const location_key = this.auth.location_key;

            const MAX_OPS_PER_BATCH = 50;

            // ✅ ahora es el doc del área (ya existente)
            const areaRef = firestore.doc(`accounts_abc/${accountKey}/areas/${area_key}`);

            // -----------------------------
            // 1) LECTURAS + CÁLCULOS (fuera de batch)
            // -----------------------------
            const plans: any[] = [];

            for (const item of materials) {
                const material = item.material;
                const measure = item.measure;
                const metric = item.metric || material.metric || null;

                const unitPrice = Number(item.unit_price || item.price || 0);
                const available_quantity = Number(item.available_quantity || item.quantity || 0);

                const locKey = location_key;
                const areaKey = area_key;

                const inventoryId = `${material.$key}_${locKey}_${areaKey}`;
                const invRef = firestore.doc(`accounts_abc/${accountKey}/inventory/${inventoryId}`);
                const costRef = firestore.doc(`accounts_abc/${accountKey}/inventory_cost/${material.$key}_${locKey}`);

                const [invSnap, costSnap] = await Promise.all([invRef.get(), costRef.get()]);

                const invUnit = invSnap.exists
                    ? ((((invSnap.data() as any)?.metric)?.symbol) || material.metric?.symbol || 'pzs')
                    : (material.metric?.symbol || 'pzs');

                const qtyBase = Number(this.abc.convertMeasure(
                    measure,
                    available_quantity,
                    (metric && metric.symbol) || material.metric?.symbol || invUnit,
                    invUnit
                ));

                if (!(qtyBase > this.EPS)) {
                    throw new Error(`Cantidad inválida para el material ${material.name}.`);
                }

                const prevAvail = invSnap.exists ? Number(((invSnap.data() as any)['available']) || 0) : 0;
                const newAvail = this.to6(prevAvail + qtyBase);

                const costExists = costSnap.exists;
                const prevPool = costExists ? Number(((costSnap.data() as any)['qty_pool']) || 0) : 0;
                const prevAvg = costExists ? Number(((costSnap.data() as any)['avg_price']) || 0) : 0;

                const batchId = this.firestore.createId();
                const batchRef = invRef.collection('batches').doc(batchId);
                const batchCode = `INI-${new Date().toISOString().slice(2, 10).replace(/-/g, '')}-${batchId.slice(-4)}`;

                const batchData = {
                    batch_key: batchId,
                    entry_date: this.ts(),
                    expiration_date: item.expiration_date || null,
                    quantity: this.to6(qtyBase),
                    unit_price: unitPrice,
                    status: 'active',
                    created_from: 'initial_setup',
                    batch_code: batchCode
                };

                const logDetailBase = {
                    batch_key: batchId,
                    batch_code: batchCode,
                    unit_price: unitPrice,
                    initial_quantity: this.to6(qtyBase),
                    initial_price: Number(item.unit_price || unitPrice),
                    expiration_date: item?.expiration_date ?? null
                };

                plans.push({
                    material, areaKey, locKey,
                    invRef, invSnapExists: invSnap.exists,
                    invUnit, prevAvail, newAvail,
                    batchRef, batchData,
                    wac_prevPool: prevPool,
                    wac_prevAvg: prevAvg,
                    unitPrice,
                    costRef, costExists,
                    logDetailBase
                });
            }

            // -----------------------------
            // 2) ESCRITURAS en batches de máx ~480 ops
            // -----------------------------
            const batches: any[] = [];
            let currentBatch = firestore.batch();
            let batchCounter = 0;

            for (const p of plans) {
                if (p.invSnapExists) {
                    currentBatch.update(p.invRef, {
                        available: p.newAvail,
                        last_purchase: this.ts()
                    });
                } else {
                    currentBatch.set(p.invRef, {
                        material_key: p.material.$key,
                        name: p.material.name,
                        sku: p.material.sku || '',
                        area_key: p.areaKey,
                        location_key: p.locKey,
                        available: p.newAvail,
                        metric: p.material.metric || null,
                        measure: p.material.measure || null,
                        type: p.material.type || null,
                        category_key: p.material.category_key || null,
                        account: accountKey,
                        creation_date: this.ts(),
                        last_purchase: this.ts()
                    });
                }
                batchCounter++;

                currentBatch.set(p.batchRef, p.batchData);
                batchCounter++;

                const entryRes = this.applyWacCoreWriteOnly(
                    currentBatch,
                    p.costRef,
                    p.material.$key, p.locKey,
                    p.costExists,
                    p.wac_prevPool, p.wac_prevAvg,
                    p.batchData.quantity,
                    p.unitPrice,
                    'initial_setup',
                    null
                );
                batchCounter++;

                this.logInventoryMovementBatch(currentBatch, {
                    type: 'initial_setup',
                    source_key: null,
                    material: p.material,
                    quantity_before: p.prevAvail,
                    quantity_after: p.newAvail,
                    quantity_changed: this.to6(p.newAvail - p.prevAvail),
                    measure: p.invUnit,
                    location_key: p.locKey,
                    area_key: p.areaKey,
                    user,
                    detail: {
                        ...p.logDetailBase,
                        avg_price_location_before: entryRes.prevAvg,
                        avg_price_location_after: entryRes.newAvg,
                        qty_pool_before: entryRes.prevPool,
                        qty_pool_after: entryRes.newPool,
                        last_unit_price: entryRes.appliedUnit
                    }
                });
                batchCounter++;

                if (batchCounter >= MAX_OPS_PER_BATCH) {
                    batches.push(currentBatch);
                    currentBatch = firestore.batch();
                    batchCounter = 0;
                }
            }

            // ✅ marcar inicialización como completada POR ÁREA
            currentBatch.set(areaRef, {
                inventory_initialization: {
                    status: 'completed',
                    completed_at: this.ts(),
                    completed_by: {
                        uid: user?.uid || null,
                        name: this.auth.user_resume || null
                    }
                }
            }, { merge: true });
            batchCounter++;

            if (batchCounter > 0) {
                batches.push(currentBatch);
            }

            // Commit secuencial para mantener orden
            for (const batch of batches) {
                await batch.commit();
            }

            return true;
        } catch (error: any) {
            console.error('Error en createInitialInventory:', error);
            throw error;
        }
    }


    async cancelProduction(
        production: any,
        returned_materials: any[],
        signature: any,
        reason: string,
        collection: 'productions' | 'sample_productions'
    ) {
        if (!collection) throw new Error("La colección debe ser 'productions' o 'sample_productions'");

        const accountKey = this.auth.account_key;
        const firestore = this.firestore.firestore;

        // 1) Subir firma fuera del tx
        const folder = `signatures/${accountKey}`;
        const signatureUrl = await this.cloudinary.uploadImage(signature, folder);

        // 2) Refs
        const productionKey = production?.$key;
        if (!productionKey) throw new Error('production.$key no viene');

        const prodRef = firestore
            .collection(`accounts_abc/${accountKey}/${collection}`)
            .doc(productionKey);

        // Guardá un snapshot limpio (evita funciones/objetos raros del UI)
        const cleanReturned = (returned_materials || []).map((m: any) => ({
            material_key: m?.material?.$key || m?.inventory?.material_key || null,
            inventory_key: m?.inventory?.$key || null,
            returned_to_inv: !!m?.returned_to_inv,
            quantity: Number(m?.quantity || 0),
            location_key: m?.origin_location || m?.inventory?.location_key || m?.location_key || null,
            area_key: m?.origin_area || m?.inventory?.area_key || m?.area_key || null,
            used_batches: (m?.used_batches || []).map((b: any) => ({
                batch_key: b?.batch_key,
                used_from_batch: Number(b?.used_from_batch || 0),
                unit_price: b?.unit_price ?? null,
                expiration_date: b?.expiration_date ?? null,
                batch_code: b?.batch_code ?? null,
            })),
            wac_snapshot: m?.wac_snapshot
                ? {
                    qty_pool_before: Number(m.wac_snapshot.qty_pool_before || 0),
                    qty_pool_after: Number(m.wac_snapshot.qty_pool_after || 0),
                    avg_price_before: Number(m.wac_snapshot.avg_price_before || 0),
                    avg_price_after: Number(m.wac_snapshot.avg_price_after || 0),
                }
                : null,
        }));

        await firestore.runTransaction(async (tx) => {
            // =========================================================
            // FASE 1: SOLO LECTURAS
            // =========================================================
            const prodSnap: any = await tx.get(prodRef);
            if (!prodSnap.exists) throw new Error('No existe la producción a cancelar');

            // (opcional) evitar doble cancelación
            const currentStatus = prodSnap.data()?.status;
            if (currentStatus === 'cancelled') return true;

            const batchOps: any = [];
            const invOpsMap = new Map(); // key = invKey
            const batchSnapMap = new Map();

            // 1) Construir lista de refs a leer (inv + batches)
            for (const m of (returned_materials || [])) {
                if (!m?.returned_to_inv) continue;

                const invKey = m?.inventory?.$key;
                if (!invKey) continue;

                const invRef = firestore.collection(`accounts_abc/${accountKey}/inventory`).doc(invKey);

                const invLocationKey = m?.origin_location || m?.inventory?.location_key || m?.location_key;
                const invAreaKey = m?.origin_area || m?.inventory?.area_key || m?.area_key;
                const ingredientKey = m?.material?.$key || m?.inventory?.material_key || null;

                // asegurar invOp
                if (!invOpsMap.has(invKey)) {
                    invOpsMap.set(invKey, {
                        invRef,
                        totalReturned: 0,
                        beforeAvail: 0,
                        ingredientKey,
                        invLocationKey,
                        invAreaKey,
                        material: m?.material || m?.inventory || null,
                        used_batches: [],
                    });
                }

                const usedBatches = Array.isArray(m?.used_batches) ? m.used_batches : [];
                for (const b of usedBatches) {
                    const amount = Number(b?.used_from_batch || 0);
                    if (amount <= 0) continue;

                    invOpsMap.get(invKey)!.totalReturned += amount;
                    invOpsMap.get(invKey)!.used_batches.push({
                        batch_key: b?.batch_key,
                        batch_code: b?.batch_code ?? null,
                        used_from_batch: amount,
                        unit_price: b?.unit_price ?? null,
                        expiration_date: b?.expiration_date ?? null,
                    });

                    const batchRef = invRef.collection('batches').doc(b.batch_key);

                    batchOps.push({
                        invRef,
                        invKey,
                        invLocationKey,
                        invAreaKey,
                        ingredientKey,
                        batchRef,
                        batchKey: b.batch_key,
                        amount,
                        unit_price: b.unit_price ?? null,
                        expiration_date: b.expiration_date ?? null,
                        batch_code: b.batch_code ?? null,
                        beforeQty: 0,
                    });
                }
            }

            // 2) Leer TODOS los inventory docs
            const invKeys = Array.from(invOpsMap.keys());
            const invSnaps = await Promise.all(invKeys.map((k) => tx.get(invOpsMap.get(k)!.invRef)));

            // guardar beforeAvail
            invSnaps.forEach((snap: any, idx) => {
                const invKey = invKeys[idx];
                if (!snap.exists) {
                    // si no existe inventario, lo ignoramos
                    invOpsMap.delete(invKey);
                    return;
                }
                invOpsMap.get(invKey)!.beforeAvail = Number(snap.data()?.available || 0);
            });

            // 3) Leer TODOS los batch docs (después de inv reads, pero todavía sin escribir)
            // Nota: si un invRef no existe, batchOps que dependan de ese invKey hay que ignorarlos
            const validInvKeySet = new Set(invOpsMap.keys());
            const validBatchOps = batchOps.filter((op: any) => validInvKeySet.has(op.invKey));

            const batchSnaps = await Promise.all(validBatchOps.map((op: any) => tx.get(op.batchRef)));

            // guardar beforeQty para cada op
            batchSnaps.forEach((snap: any, idx) => {
                const op = validBatchOps[idx];
                const beforeQty = snap.exists ? Number(snap.data()?.quantity || 0) : 0;
                op.beforeQty = beforeQty;
                batchSnapMap.set(op.batchRef.path, snap);
            });

            // =========================================================
            // FASE 2: SOLO ESCRITURAS
            // =========================================================
            // A) aplicar batch updates/sets + movement docs
            for (const op of validBatchOps) {
                const afterQty = op.beforeQty + op.amount;
                const batchSnap = batchSnapMap.get(op.batchRef.path);

                if (batchSnap?.exists) {
                    const bData = batchSnap.data() as any;
                    const patch: any = { quantity: afterQty, status: 'active' };
                    if (bData?.status === 'depleted' && afterQty > this.EPS) {
                        patch.depleted_date = null;
                        patch.activation_date = this.ts();
                    }
                    tx.update(op.batchRef, patch);
                } else {
                    tx.set(op.batchRef, {
                        batch_key: op.batchKey,
                        entry_date: this.ts(),
                        expiration_date: op.expiration_date,
                        quantity: op.amount,
                        unit_price: op.unit_price,
                        status: 'active',
                        created_from: 'production_cancel',
                        source_key: productionKey,
                        batch_code: op.batch_code,
                    });
                }
            }

            // B) actualizar available + inventory_cost.qty_pool (por locación)
            for (const [invKey, invOp] of invOpsMap.entries()) {
                if (invOp.totalReturned <= 0) continue;

                tx.update(invOp.invRef, {
                    available: invOp.beforeAvail + invOp.totalReturned,
                });

                const afterAvail = Number(invOp.beforeAvail || 0) + Number(invOp.totalReturned || 0);
                const movementMaterial = invOp.material || {
                    $key: invOp.ingredientKey,
                    material_key: invOp.ingredientKey,
                    name: null,
                };

                this.logInventoryMovementBatch(tx, {
                    type: 'production_cancel',
                    source_key: productionKey,
                    material: movementMaterial,
                    quantity_before: invOp.beforeAvail,
                    quantity_after: afterAvail,
                    quantity_changed: invOp.totalReturned,
                    measure: movementMaterial?.metric?.symbol || movementMaterial?.measure || null,
                    location_key: invOp.invLocationKey || null,
                    area_key: invOp.invAreaKey || null,
                    user: this.auth.user_resume,
                    detail: {
                        direction: 'in',
                        returned_quantity: invOp.totalReturned,
                        used_batches: invOp.used_batches || [],
                    },
                });

                if (invOp.ingredientKey && invOp.invLocationKey) {
                    const costRef = firestore
                        .collection(`accounts_abc/${accountKey}/inventory_cost`)
                        .doc(`${invOp.ingredientKey}_${invOp.invLocationKey}`);

                    tx.set(
                        costRef,
                        {
                            ingredient_key: invOp.ingredientKey,
                            location_key: invOp.invLocationKey,
                            qty_pool: firebase.firestore.FieldValue.increment(invOp.totalReturned),
                            last_entry: this.ts(),
                            updated_at: this.ts(),
                        },
                        { merge: true }
                    );
                }
            }

            // C) cancelar producción
            tx.update(prodRef, {
                status: 'cancelled',
                cancelled_by: this.auth.user_resume,
                cancelled_date: this.ts(),
                cancelled_signature: signatureUrl,
                cancelled_reason: reason,
                returned_materials: cleanReturned,
            });

            return true;
        });

        return true;
    }




    addABCLocation(location: any, areas: any) {
        return new Promise((resolve, reject) => {

            let batch = this.firestore.firestore.batch();

            let location_key = this.firestore.createId();
            batch.set(this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/locations`).doc(location_key), location);

            areas.forEach((area: any) => {
                const area_key = this.firestore.createId();
                area.location_key = location_key;

                batch.set(this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/areas`).doc(area_key), area);
            });

            batch.commit().then(data => {
                resolve(true);
            }, err => {
                console.log(err);
                reject();
            })


        });
    }

    getDeviceCode() {
        return new Promise((resolve, reject) => {
            let code = this.makeDeviceCode();

            this.getRef('devices').ref
                .where('code', '==', code)
                .where('type', '==', 'ABC')
                .get()
                .then((snapshots: any) => {
                    if (snapshots.empty) {
                        resolve(code);
                    } else {
                        reject();
                    }
                })
        });
    }

    makeDeviceCode() {
        var result = '';
        var characters = '0123456789';
        var charactersLength = characters.length;
        for (var i = 0; i < 5; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        result = `${result}-${characters.charAt(Math.floor(Math.random() * charactersLength))}`
        return result;
    }

    addDevice(data: any) {
        return new Promise((resolve, reject) => {
            let batch = this.firestore.firestore.batch();

            let device_key = this.firestore.createId();

            batch.set(this.firestore.firestore.collection(`accounts_abc/${this.auth.account_key}/devices`).doc(device_key), data);

            let device = data;
            device.account_key = this.auth.account_key;

            batch.set(this.firestore.firestore.collection(`devices_abc`).doc(device_key), device);

            batch.commit().then(data => {
                resolve(device_key);
            }, err => {
                console.log(err);
                reject();
            })
        });
    }

    getUserCode() {
        return new Promise((resolve, reject) => {
            let code = this.makeUserCode();
            this.getRef(`accounts_abc/${this.auth.account}/users`).ref
                .where('code', '==', code)
                .get()
                .then((snapshots: any) => {
                    if (snapshots.empty) {
                        resolve(code);
                    } else {
                        reject();
                    }
                })
        });
    }

    makeUserCode() {
        var result = '';
        var characters = '0123456789';
        var charactersLength = characters.length;
        for (var i = 0; i < 4; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    // FUNCTIONS -- FEATURE INVENTORY COUNTING
    async initInventoryCounting(payload: any, user: any, signature: any) {
        const firestore = this.firestore.firestore;
        const batch = firestore.batch();
        const id = this.firestore.createId();
        const ref = firestore.doc(`accounts_abc/${this.auth.account_key}/inventory_counting/${id}`);

        try {
            const response = await this.verifyInventoriCounting();
            if (response) return response;

            const folder = `signatures/${this.auth.account_key}`;
            const signature_url = await this.cloudinary.uploadImage(signature, folder);

            batch.set(ref, {
                ...payload,
                started_by: user,
                started_signature_url: signature_url,
                started_date: this.ts(),
                active: true,
            });

            batch.commit()

            return id;
        } catch (error) {
            console.error('Error initializing inventory counting:', error);
            throw new Error('Error initializing inventory counting');
        }
    }

    async verifyInventoriCounting() {
        try {
            const invQuery = this.getRef(`accounts_abc/${this.auth.account_key}/inventory_counting`).ref;
            const snapshot = await invQuery
                .where('area_key', '==', this.auth.area_key)
                .where('active', '==', true)
                .limit(1)
                .get();

            if (snapshot.empty) return false;

            const doc: any = snapshot.docs[0];
            return { id: doc.id, ...doc.data() };
        } catch (error) {
            console.error('Error en verifyInventoriCounting:', error);
            throw new Error('Error verifying inventory counting');
        }
    }

    async finishInventoryCounting(counting_key: string, materials: any, user: any, signature: any) {
        const firestore = this.firestore.firestore;
        const accountKey = this.auth.account_key;
        const locationKey = this.auth.location_key;
        const ref = firestore.doc(`accounts_abc/${accountKey}/inventory_counting/${counting_key}`);

        try {
            return await firestore.runTransaction(async (tx) => {
                const snapshot = await tx.get(ref);
                if (!snapshot.exists) throw new Error("Documento no encontrado.");

                const folder = `signatures/${accountKey}`;
                const signature_url = await this.cloudinary.uploadImage(signature, folder);
                const data: any = snapshot.data();

                const updateFields = {
                    finished_by: user,
                    finished_signature_url: signature_url,
                    finished_date: this.ts(),
                    status: "finished",
                    materials,
                };

                const materialsData: any[] = [];

                if (!Array.isArray(materials)) return;

                for (const material of materials) {
                    const matRef = firestore.doc(`accounts_abc/${accountKey}/inventory/${material.inventory_key}`);
                    const matSnap = await tx.get(matRef);
                    if (!matSnap.exists) throw new Error(`Material ${material.name} no encontrado.`);

                    const costRef = firestore.doc(
                        `accounts_abc/${accountKey}/inventory_cost/${material.material_key}_${locationKey}`
                    );
                    const costSnap = await tx.get(costRef);
                    const costData: any = costSnap.exists ? costSnap.data() : null;

                    const batchesSnap = await matRef.collection("batches")
                        .orderBy("entry_date", "asc")
                        .get();

                    const batchesList = batchesSnap.docs.map(doc => ({
                        ref: doc.ref,
                        data: doc.data()
                    }));

                    materialsData.push({
                        matRef,
                        costRef,
                        costExists: !!costSnap.exists,
                        costData,
                        costSnap,
                        batchesList,
                        material,
                        area: this.auth.area

                    });
                }

                const writeOps: any[] = [];
                const movementLogs: any[] = [];
                // const poolChanges: Map<string, { costRef: any, prevPool: number, totalDiff: number }> = new Map();

                for (const item of materialsData) {
                    const { matRef, costRef, costExists, costData, costSnap, batchesList, material, area } = item;
                    console.log(material)
                    const beforeAvailable = material.available;
                    const afterAvailable = material.new_available;
                    let remainingChange = afterAvailable - beforeAvailable;

                    const direction = remainingChange > 0 ? "in" :
                        remainingChange < 0 ? "out" : "none";

                    const batchMutations: any[] = [];

                    // ---------------------------
                    // FIFO OUT
                    // ---------------------------
                    if (remainingChange < 0) {
                        let toRemove = Math.abs(remainingChange);

                        for (const batch of batchesList) {
                            if (toRemove <= 0) break;
                            const qty = batch.data.quantity || 0;
                            if (qty <= 0) continue;

                            const remove = Math.min(qty, toRemove);
                            const newQty = qty - remove;
                            toRemove -= remove;

                            const status = newQty <= 0 ? "depleted" : "active";
                            batchMutations.push({
                                type: "update",
                                ref: batch.ref,
                                data: {
                                    quantity: this.to6(newQty),
                                    status,
                                    depleted_date: status === "depleted" ? this.ts() : null
                                }
                            });
                        }

                        remainingChange = -toRemove;
                    }

                    // ---------------------------
                    // IN → NEW BATCH
                    // ---------------------------
                    if (remainingChange > 0) {
                        const diff = remainingChange;

                        const batchRef = matRef.collection("batches").doc();
                        const batchId = batchRef.id;
                        const batchCode = `PUR-${new Date().toISOString().slice(2, 10).replace(/-/g, '')}-${batchId.slice(-4)}`;

                        const newBatch = {
                            batch_key: batchId,
                            entry_date: this.ts(),
                            expiration_date: null,
                            quantity: this.to6(diff),
                            unit_price: Number(costData?.avg_price || 0),
                            status: "active",
                            created_from: "inventory_counting",
                            source_key: counting_key,
                            batch_code: batchCode,
                        };

                        batchMutations.push({
                            type: "set",
                            ref: batchRef,
                            data: newBatch
                        });

                        remainingChange = 0;
                    }

                    const prevPool = costExists ? Number(((costSnap.data() as any)['qty_pool']) || 0) : 0;
                    const prevAvg = costExists ? Number(((costSnap.data() as any)['avg_price']) || 0) : 0;

                    // INVENTORY_COST (WAC) - sin lecturas adicionales, usando helper
                    const entryRes = this.applyWacCoreWriteOnly(
                        tx,
                        costRef,
                        material.material_key,
                        locationKey,
                        costExists,
                        prevPool,
                        prevAvg,
                        this.to6(afterAvailable - beforeAvailable),
                        prevAvg,
                        'inventory_counting',
                        counting_key
                    );
                    // ---------------------------
                    // ACTUALIZAR INVENTARIO
                    // ---------------------------
                    writeOps.push({ type: "update", ref: matRef, data: { available: afterAvailable } });
                    for (const bm of batchMutations) writeOps.push(bm);



                    movementLogs.push({
                        type: "inventory_count",
                        source_key: matRef.id,
                        material,
                        counting_key,
                        quantity_before: beforeAvailable,
                        quantity_after: afterAvailable,
                        quantity_changed: afterAvailable - beforeAvailable,
                        measure: material.measure,
                        location_key: locationKey,
                        area_key: area.$key,
                        user: this.auth.user_resume,
                        avg_price_location: costData?.avg_price,
                        detail: {
                            price: costData?.avg_price ?? 0,
                            transfer_type: "inventory_count",
                            direction,
                            started_signature_url: data.started_signature_url,
                            started_date: data.started_date,
                            started_by: data.started_by,
                            ...updateFields,
                            avg_price_location_before: entryRes.prevAvg,
                            avg_price_location_after: entryRes.newAvg,
                            qty_pool_before: entryRes.prevPool,
                            qty_pool_after: entryRes.newPool,
                            last_unit_price: entryRes.appliedUnit
                        }
                    });
                }



                // ---------------------------
                // WRITE PHASE
                // ---------------------------
                tx.update(ref, { active: false, ...updateFields });
                for (const op of writeOps) {
                    if (op.type === "update") tx.update(op.ref, op.data);
                    else if (op.type === "set") tx.set(op.ref, op.data);
                }
                for (const movement of movementLogs) this.logInventoryMovementBatch(tx, movement);

                return true;
            });

        } catch (error) {
            console.error("Error finishing location counting:", error);
            throw new Error("Error finishing location counting");
        }
    }


    async getAvgCostByLocation(materialKey: string, locationKey: string): Promise<number> {
        const accountKey = this.auth.account_key;
        const firestore = this.firestore.firestore;

        const costId = `${materialKey}_${locationKey}`;
        const costRef = firestore.doc(`accounts_abc/${accountKey}/inventory_cost/${costId}`);
        const snap = await costRef.get();

        if (!snap.exists) return 0;
        const data = snap.data() as any;
        return Number(data?.avg_price || 0);
    }

    async adjustLocationQtyPool(batch: any, materialKey: any, locationKey: any, deltaQty: any, opts?: any
    ): Promise<void> {
        const accountKey = this.auth.account_key;
        const firestore = this.firestore.firestore;

        const costId = `${materialKey}_${locationKey}`;
        const costRef = firestore.doc(`accounts_abc/${accountKey}/inventory_cost/${costId}`);
        const snap: any = await costRef.get();

        const prevPool = snap.exists ? Number(snap.data()?.qty_pool || 0) : 0;
        const newPool = Math.max(0, Number((prevPool + Number(deltaQty || 0)).toFixed(6)));

        if (!snap.exists) {
            // Si no existe y delta es positiva, creamos una “carcasa” (caso raro en salidas).
            batch.set(costRef, {
                material_key: materialKey,
                location_key: locationKey,
                avg_price: 0,                  // sin entradas previas, no sabemos el costo
                qty_pool: newPool,
                last_entry: opts?.mark === 'in' ? this.ts() : null,
                last_out: opts?.mark === 'out' ? this.ts() : null,
                last_source: opts?.source_key || null
            });
        } else {
            const patch: any = {
                qty_pool: newPool,
                last_source: opts?.source_key || snap.data()?.last_source || null
            };
            if (opts?.mark === 'in') patch.last_entry = this.ts();
            if (opts?.mark === 'out') patch.last_out = this.ts();

            batch.update(costRef, patch);
        }
    }

    // ── Utilidades ─────────────────────────────────────────────
    private EPS = 1e-6;
    public to6(n: number): number { return Number(n.toFixed(6)); }

    // ── WAC Entry: entradas SÍ recalculan promedio (compras, transfer_in, outputs) ──


    /** ID canónico para inventory_cost: un doc por (material, location) */


    /** ENTRY write-only (sin lecturas) — sirve para purchase, transfer_in, production_in, etc. */
    private applyWacCoreWriteOnly(
        tx: any,
        costRef: any,
        material_key: string,
        location_key: string,
        exists: boolean,
        prevPool: number,
        prevAvg: number,
        qtyIn: number,
        unitIn: number,
        source: string,
        sourceKey: any
    ) {
        qtyIn = this.to6(qtyIn);
        unitIn = Number(unitIn || 0);

        const newPool = this.to6(prevPool + qtyIn);
        const newAvg = newPool > this.EPS
            ? this.to6((prevPool * prevAvg + qtyIn * unitIn) / newPool)
            : unitIn;

        const payload = {
            material_key, location_key,
            avg_price: newAvg,
            qty_pool: newPool,
            last_entry: this.ts(),
            last_source: source,
            last_source_key: sourceKey,
            last_unit_price: unitIn
        };

        if (!exists) tx.set(costRef, payload); else tx.update(costRef, payload);
        return { prevPool, prevAvg, newPool, newAvg, appliedUnit: unitIn };
    }

    /** EXIT write-only (sin lecturas) — sirve para transfer_out, sales, withdrawals, etc. */
    private applyWacExitCoreWriteOnly(
        tx: any,
        costRef: any,
        material_key: string,
        location_key: string,
        exists: boolean,
        prevPool: number,
        prevAvg: number,
        qtyOut: number,
        source: string,
        sourceKey: string
    ) {
        qtyOut = this.to6(qtyOut);
        const take = Math.min(prevPool, qtyOut);
        const newPool = this.to6(prevPool - take);
        const newAvg = newPool > this.EPS ? prevAvg : prevAvg; // el avg se conserva en salidas

        const payload = {
            material_key, location_key,
            avg_price: newAvg,
            qty_pool: newPool,
            last_exit: this.ts(),
            last_source: source,
            last_source_key: sourceKey,
            last_unit_price: prevAvg
        };

        if (!exists) tx.set(costRef, payload); else tx.update(costRef, payload);
        return { prevPool, prevAvg, newPool, newAvg, appliedUnit: prevAvg };
    }

    /** Wrappers COMPATIBLES (con lecturas internas) — no rompen tus flujos antiguos */
    private async applyWacEntry(
        tx: any,
        costRef: any,
        qtyIn: number,
        unitIn: number,
        source: string,
        sourceKey: string
    ) {
        const snap = await tx.get(costRef);
        const exists = snap.exists;
        const prevPool = exists ? Number((snap.data() as any).qty_pool || 0) : 0;
        const prevAvg = exists ? Number((snap.data() as any).avg_price || 0) : 0;
        const [material_key, location_key] = costRef.id.split('_');
        return this.applyWacCoreWriteOnly(
            tx, costRef, material_key, location_key, exists, prevPool, prevAvg, qtyIn, unitIn, source, sourceKey
        );
    }

    private async applyWacExit(
        tx: any,
        costRef: any,
        qtyOut: number,
        source: string,
        sourceKey: string
    ) {
        const snap = await tx.get(costRef);
        const exists = snap.exists;
        const prevPool = exists ? Number((snap.data() as any).qty_pool || 0) : 0;
        const prevAvg = exists ? Number((snap.data() as any).avg_price || 0) : 0;
        const [material_key, location_key] = costRef.id.split('_');
        return this.applyWacExitCoreWriteOnly(
            tx, costRef, material_key, location_key, exists, prevPool, prevAvg, qtyOut, source, sourceKey
        );
    }

    async getInventoryBatchHistoryById(id: string) {
        const inventoryRef = this.firestore.firestore.doc(
            `accounts_abc/${this.auth.account_key}/inventory/${id}`
        );

        const inventorySnapshot = await inventoryRef.get();
        if (!inventorySnapshot.exists) {
            throw new Error(`No se encontró un inventario en Firestore con id: ${id}`);
        }

        const inventory: any = inventorySnapshot.data()!;
        const batchesSnapshot = await inventoryRef
            .collection('batches')
            .orderBy('entry_date', 'desc')
            .limit(100)
            .get();

        const batches = batchesSnapshot.docs.map((doc) => {
            const data: any = doc.data();
            const entryDate = data?.entry_date?.toDate ? data.entry_date.toDate() : (data?.entry_date ? new Date(data.entry_date) : null);
            const expirationDate = data?.expiration_date?.toDate ? data.expiration_date.toDate() : (data?.expiration_date ? new Date(data.expiration_date) : null);
            const depletedDate = data?.depleted_date?.toDate ? data.depleted_date.toDate() : (data?.depleted_date ? new Date(data.depleted_date) : null);
            const activationDate = data?.activation_date?.toDate ? data.activation_date.toDate() : (data?.activation_date ? new Date(data.activation_date) : null);

            return {
                $key: doc.id,
                ...data,
                entry_date: entryDate,
                expiration_date: expirationDate,
                depleted_date: depletedDate,
                activation_date: activationDate,
                quantity: Number(data?.quantity || 0),
                metric: inventory.metric,
                measure: inventory.measure
            };
        });

        return { batches };
    }

    uploadToCloudinary(file: any) {
        return new Promise((resolve, reject) => {

            const timestamp = Math.round((new Date).getTime() / 1000);

            let public_id = this.makeid(20);

            const formData = new FormData();
            formData.append("file", file);
            formData.append("api_key", environment.cloudinary.api_key);
            formData.append("public_id", public_id);
            formData.append("return_delete_token", "1");
            formData.append("timestamp", String(timestamp));
            var signature = CryptoJS.SHA1(`public_id=${public_id}&return_delete_token=1&timestamp=${String(timestamp)}${environment.cloudinary.api_secret}`);
            formData.append("signature", String(signature));
            this.http.post(environment.cloudinary.upload.url, formData)
                .subscribe(response => {
                    resolve(response);
                }, error => {
                    reject(error);
                });

        })
    }

    deleteToCloudinary(image: any) {
        return new Promise((resolve, reject) => {
            const timestamp = Math.round((new Date).getTime() / 1000);

            const formData = new FormData();

            formData.append("api_key", environment.cloudinary.api_key);
            formData.append("public_id", image.public_id);
            formData.append("timestamp", String(timestamp));
            var signature = CryptoJS.SHA1(`public_id=${image.public_id}&timestamp=${String(timestamp)}${environment.cloudinary.api_secret}`);
            formData.append("signature", String(signature));
            this.http.post(environment.cloudinary.delete.url, formData)
                .subscribe(response => {
                    resolve(response);
                }, error => {
                    reject(error);
                });

        })
    }
    makeid(length: any) {
        var result = '';
        var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        var charactersLength = characters.length;
        for (var i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() *
                charactersLength));
        }
        return result;
    }
}

